import {Component, Inject, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone} from '@angular/core';
import {FormBuilder, FormGroup, Validators, FormControl} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialog} from '@angular/material/dialog';
import {DialogComponent} from '@vex/components/dialog/dialog.component';
import {ProjectTreeNode, ProjectTreeNodeCreateUpdate} from '../project-tree-node.interface';
import {ProjectFormData, ProjectType, ProjectOrigin, ProjectClient, ProjectCurrency} from '../project-form-data.interface';
import {mapFrontendToBackend, mapBackendToFrontend} from '../project-mapper.util';
import {ProjectsService} from '@core/services/backend/common/service/projects.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {TableColumn} from '@vex/interfaces/table-column.interface';
import {ClientsService} from '@core/services/backend/common/service/clients.service';
import {ProjectTypesService} from '@core/services/backend/common/service/project-types.service';
import {OriginsService} from '@core/services/backend/common/service/origins.service';
import {CurrenciesService} from '@core/services/backend/common/service/currencies.service';
import {UsersService} from '@core/services/backend/common/service/users.service';
import {User} from '@core/services/backend/common/interface/users';
import {LevelsService} from '@core/services/backend/common/service/levels.service';
import {Level} from '@core/services/backend/common/interface/levels';
import {Subject, takeUntil, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

export interface ProjectNodeCreateUpdateData {
    node?: ProjectTreeNode;
    parentId?: string;
    parentNode?: ProjectTreeNode; // Nodo padre completo para acceder a su código
    level: number;
    allNodes?: ProjectTreeNode[];
    onCreateSibling?: () => void; // Callback para crear un hermano
}

@Component({
    selector: 'vex-project-node-create-update',
    templateUrl: './project-node-create-update.component.html',
    styleUrls: ['./project-node-create-update.component.scss']
})
export class ProjectNodeCreateUpdateComponent implements OnInit, AfterViewInit, OnDestroy {

    form!: FormGroup;
    mode: 'create' | 'update';
    isLoading = false;
    private readonly destroying$ = new Subject<void>();

    // Grilla de hermanos
    siblingsDataSource: MatTableDataSource<ProjectTreeNode>;
    siblingsTableKey = 0; // Clave para forzar re-render completo de la tabla
    siblingsColumns: TableColumn<ProjectTreeNode>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.NOMBRE', property: 'name', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'GRIDS.CODIGO', property: 'code', type: 'text', visible: true},
        {label: 'GRIDS.ACTIVO', property: 'active', type: 'image', visible: true},
        {label: 'GRIDS.DESCRIPCION', property: 'description', type: 'text', visible: false},
        {label: 'GRIDS.NIVEL', property: 'level', type: 'text', visible: false}
    ];
    @ViewChild('siblingsPaginator', {static: false}) siblingsPaginator: MatPaginator;
    @ViewChild('siblingsSort', {static: false}) siblingsSort: MatSort;

    // Control de tabs
    selectedTabIndex = 0; // 0 = Detalle, 1 = Hijos
    
    // Filtro para hijos
    childrenFilterControl = new FormControl('');

    // Datos para los dropdowns - se cargan desde los servicios
    projectTypes: ProjectType[] = [];
    projectOrigins: ProjectOrigin[] = [];
    projectClients: ProjectClient[] = [];
    projectCurrencies: ProjectCurrency[] = [];
    availableCodes: Level[] = []; // Códigos disponibles según el nivel seleccionado
    glosaCode: string = ''; // Campo para la glosa cuando es tipo proyecto

    // Lista de usuarios para autocomplete
    userListAuto: User[] = [];
    filteredJefeProyectoList: Observable<User[]> = new Observable<User[]>();
    filteredControlProyectoList: Observable<User[]> = new Observable<User[]>();
    filteredControlDocumentalList: Observable<User[]> = new Observable<User[]>();

    // Lista de proyectos sin padre para seleccionar al crear un hijo
    availableProjectsWithoutParent: ProjectTreeNode[] = [];
    filteredProjectsWithoutParent: Observable<ProjectTreeNode[]> = new Observable<ProjectTreeNode[]>();
    selectedExistingProject: ProjectTreeNode | null = null;
    existingProjectInputControl = new FormControl('');

    // Selector de padre en tab Padre/Hijos (modo edición)
    availableParentList: ProjectTreeNode[] = [];
    parentAutocompleteControl = new FormControl<ProjectTreeNode | null>(null);
    filteredParentList: Observable<ProjectTreeNode[]> = new Observable<ProjectTreeNode[]>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ProjectNodeCreateUpdateData,
        private dialogRef: MatDialogRef<ProjectNodeCreateUpdateComponent>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private projectsService: ProjectsService,
        private snackbar: MatSnackBar,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private clientsService: ClientsService,
        private projectTypesService: ProjectTypesService,
        private originsService: OriginsService,
        private currenciesService: CurrenciesService,
        private usersService: UsersService,
        private levelsService: LevelsService
    ) {
        this.mode = data.node ? 'update' : 'create';
        
        // Deshabilitar el cierre automático del diálogo
        this.dialogRef.disableClose = true;
        
        // Inicializar siblingsDataSource con array vacío para evitar errores en el getter
        this.siblingsDataSource = new MatTableDataSource<ProjectTreeNode>([]);
    }

    // Propiedades para manejar código heredado y editable
    parentCodePrefix: string = ''; // Parte heredada del padre (ej: "PROY-001-")
    editableCodeSuffix: string = ''; // Parte editable (ej: "001")

    ngOnInit(): void {
        // Separar código en parte heredada y editable si hay un padre
        const hasParent = (this.isCreateMode && (this.data.parentNode || this.data.parentId)) || 
                         (this.isUpdateMode && this.data.node?.parentId);
        
        if (hasParent) {
            // Si es creación, generar código automáticamente y extraer el sufijo
            if (this.isCreateMode) {
                if (this.data.parentNode?.code) {
                    // Si hay parentNode con código, usarlo directamente
                    const fullCode = this.generateChildCode();
                    const parentCode = this.data.parentNode.code || '';
                    const codeParts = this.splitCode(fullCode, parentCode);
                    this.parentCodePrefix = codeParts.prefix;
                    this.editableCodeSuffix = codeParts.suffix;
                } else if (this.data.parentId) {
                    // Si hay parentId pero no parentNode, establecer el prefijo vacío por ahora
                    // Se establecerá cuando se cargue el parentNode o cuando se guarde
                    // Intentar obtener el código del padre si es posible
                    // Por ahora, establecer un prefijo vacío y se completará en save()
                    this.parentCodePrefix = '';
                }
            } else if (this.isUpdateMode && this.data.node?.code) {
                // Si es edición, primero intentar usar los campos separados si están disponibles
                // Esto es especialmente útil para niveles > 1
                const nodeData = this.data.node as any;
                // Verificar explícitamente si los campos existen (no undefined/null), no si tienen valor
                // porque codigoGlosa puede estar vacío y aún así debemos usarlo
                const hasSeparateFields = nodeData.codigoPadre !== undefined || 
                                         nodeData.codigoGlosa !== undefined || 
                                         nodeData.codigoLista !== undefined;
                
                if (hasSeparateFields) {
                    // Los campos separados están disponibles, usarlos directamente
                    // Incluso si están vacíos, los usamos para mantener la consistencia
                    // IMPORTANTE: parentCodePrefix debe ser SIEMPRE el código completo del padre
                    // (ej: "3318-ODS64-0"), no solo "3318"
                    // Si codigoPadre está guardado como solo "3318", usar el código completo del parentNode
                    const savedCodigoPadre = nodeData.codigoPadre !== undefined ? (nodeData.codigoPadre || '') : '';
                    const parentNodeCode = this.data.parentNode?.code || '';
                    
                    // SIEMPRE usar el código completo del padre como prefijo si está disponible
                    // Esto es necesario porque codigo_padre puede estar guardado como solo "3318"
                    // pero necesitamos mostrar el código completo "3318-ODS64-0" como prefijo
                    if (parentNodeCode) {
                        this.parentCodePrefix = parentNodeCode;
                    } else {
                        // Solo usar savedCodigoPadre si no hay código del padre disponible
                        this.parentCodePrefix = savedCodigoPadre;
                    }
                    
                    this.editableCodeSuffix = nodeData.codigoGlosa !== undefined ? (nodeData.codigoGlosa || '') : '';
                    this.glosaCode = nodeData.codigoLista !== undefined ? (nodeData.codigoLista || '') : '';
                } else {
                    // Si no hay campos separados, extraer del código completo (compatibilidad con datos antiguos)
                let parentCode: string | null = null;
                if (this.data.parentNode?.code) {
                    parentCode = this.data.parentNode.code;
                } else {
                    parentCode = this.getParentCodeForNode(this.data.node);
                }
                
                if (parentCode) {
                        // Para tipo proyecto: Codigo Padre + "-" + Glosa + "-" + Codigo de la lista
                        // El código completo se guarda con guiones: padre-glosa-codigoLista
                        if (this.data.node.code.startsWith(parentCode)) {
                            const remaining = this.data.node.code.substring(parentCode.length);
                            this.parentCodePrefix = parentCode; // Sin guion al final
                            
                            // Si el código tiene formato con guiones (padre-glosa-codigoLista)
                            // Para niveles > 1, necesitamos usar los códigos disponibles para identificar correctamente
                            // el código de la lista, ya que el código del padre ya es completo
                            if (remaining.startsWith('-')) {
                                // Guardar el código restante temporalmente
                                // Se extraerá correctamente cuando se carguen los códigos disponibles
                                this.editableCodeSuffix = remaining.substring(1);
                                this.glosaCode = ''; // Se llenará cuando se carguen los códigos y se identifique
                            } else {
                                // Formato antiguo sin guiones - intentar extraer cuando se carguen los códigos
                                this.editableCodeSuffix = remaining;
                                this.glosaCode = ''; // Se llenará cuando se carguen los tipos y códigos
                            }
                        } else {
                            // Para otros tipos: usar lógica normal (con guion)
                    const codeParts = this.splitCode(this.data.node.code, parentCode);
                    this.parentCodePrefix = codeParts.prefix;
                    this.editableCodeSuffix = codeParts.suffix;
                        }
                } else {
                    // Si no se puede obtener el código del padre, usar el código completo como editable
                    this.parentCodePrefix = '';
                    this.editableCodeSuffix = this.data.node.code;
                    }
                }
            }
        } else {
            // Nodo raíz: código completamente editable
            this.parentCodePrefix = '';
            this.editableCodeSuffix = this.data.node?.code || '';
        }

        // Cargar datos de los mantenedores
        this.loadMaintainerData();

        // Determinar el valor inicial del código
        // Para tipo proyecto, el campo código mostrará solo la glosa (parte editable)
        // El código completo se construye como: padre-glosa-codigoLista
        // IMPORTANTE: Usar los valores que se establecieron en ngOnInit antes de crear el formulario
        let codigoInicial = this.editableCodeSuffix || '';
        let codigoListaInicial = this.glosaCode || '';

        this.form = this.fb.group({
            proyecto: [this.data.node?.name || '', [Validators.required, Validators.minLength(2)]],
            codigo: [codigoInicial, []], // Campo editable de código (glosa/segmento para tipo proyecto)
            codigoLista: [codigoListaInicial, []], // Campo para la lista seleccionable cuando es tipo proyecto
            pep: [this.data.node?.pep || this.getInheritedPep() || ''],
            aprobado: [this.data.node?.aprobado || false],
            activo: [this.data.node?.active !== false],
            visible: [this.data.node?.visible !== false],
            requiereObservacion: [this.data.node?.requiereObservacion || false],
            horasExtras: [this.data.node?.horasExtras || false],
            horasNormales: [this.data.node?.horasNormales !== false],
            tipo: [this.data.node?.tipo || this.data.parentNode?.tipo || '', []],
            id_cliente: [this.data.node?.id_cliente || this.data.parentNode?.id_cliente || '', []],
            origen: [this.data.node?.origen || this.data.parentNode?.origen || '', []],
            moneda: [this.data.node?.moneda || this.data.parentNode?.moneda || '', []],
            fechaInicio: [this.data.node?.fechaInicio || ''],
            fechaTermino: [this.data.node?.fechaTermino || ''],
            fechaEstimadaTermino: [this.data.node?.fechaEstimadaTermino || ''],
            id_jefeProyecto: [null],
            id_controlProyecto: [null],
            id_controlDocumental: [null],
            HHEstimadas: [this.data.node?.HHEstimadas || null],
            existingProjectId: [null] // Campo para seleccionar proyecto existente (solo en modo creación con padre)
        });

        // Bloquear tipo si es hijo (hereda del padre y no se puede cambiar)
        if (this.isChildNode) {
            this.form.get('tipo')?.disable();
        }

        // Suscribirse a cambios en el tipo de proyecto para actualizar la lógica del código
        this.form.get('tipo')?.valueChanges.pipe(takeUntil(this.destroying$)).subscribe((tipoId: string) => {
            this.updateCodeFieldBasedOnType(tipoId);
            this.cdr.markForCheck(); // Forzar actualización de la vista
        });

        // Suscribirse a cambios en codigo y codigoLista para actualizar el label del código completo
        this.form.get('codigo')?.valueChanges.pipe(takeUntil(this.destroying$)).subscribe(() => {
            this.cdr.markForCheck();
        });

        this.form.get('codigoLista')?.valueChanges.pipe(takeUntil(this.destroying$)).subscribe((codigoLista: string) => {
            if (codigoLista && this.isCreateMode && this.data.parentNode) {
                this.autoGenerateNameFromSubcode(codigoLista);
            }
            this.cdr.markForCheck();
        });

        // Inicializar el campo código según el tipo actual (del nodo o heredado del padre)
        const tipoInicial = this.data.node?.tipo || this.data.parentNode?.tipo;
        if (tipoInicial) {
            this.updateCodeFieldBasedOnType(tipoInicial);
        }

        // Suscribirse a cambios en el nombre del proyecto para actualizar el título
        if (this.isUpdateMode) {
            this.form.get('proyecto')?.valueChanges.subscribe(() => {
                this.cdr.markForCheck();
            });
        }

        // Inicializar observables filtrados para usuarios (se inicializarán después de cargar usuarios)
        // Los observables se configurarán en initializeUserAutocompletes() después de cargar usuarios

        // Inicializar grilla de hijos solo en modo edición
        // Detectar si se está editando un hijo (tiene parentId) para mostrar el tab de detalle por defecto
        // Si es un hijo (tiene parentId), mostrar el tab de detalle (índice 0) por defecto
        // Si es un nodo raíz, también mostrar el tab de detalle por defecto
        this.selectedTabIndex = 0;
        
        // Configurar el filtro de hijos
        this.setupChildrenFilter();
        
        if (this.isUpdateMode) {
            // Cargar hijos desde el backend para asegurar que se muestren todos
            this.loadChildrenFromBackend();
        }

        // Cargar proyectos sin padre si estamos en modo creación con un padre
        if (this.isCreateMode && this.data.parentNode) {
            this.loadProjectsWithoutParent();
        }

        // Configurar autocomplete para proyectos sin padre
        this.filteredProjectsWithoutParent = this.existingProjectInputControl.valueChanges.pipe(
            startWith(''),
            map((value: string | ProjectTreeNode | null) => {
                let searchTerm = '';
                if (typeof value === 'string') {
                    searchTerm = value;
                } else if (value && typeof value === 'object') {
                    searchTerm = (value as ProjectTreeNode).name || (value as ProjectTreeNode).code || '';
                }
                return this._filterProjectsWithoutParent(searchTerm);
            })
        );

        // Configurar autocomplete para selector de padre (tab Padre/Hijos, modo edición)
        this.filteredParentList = this.parentAutocompleteControl.valueChanges.pipe(
            startWith(this.parentAutocompleteControl.value),
            map((value: string | ProjectTreeNode | null) => {
                let searchTerm = '';
                if (typeof value === 'string') {
                    searchTerm = value;
                } else if (value && typeof value === 'object') {
                    searchTerm = (value as ProjectTreeNode).name || (value as ProjectTreeNode).code || '';
                }
                return this._filterParentList(searchTerm);
            })
        );

        if (this.isUpdateMode) {
            // Si tiene parentId pero no parentNode, cargar el padre para mostrarlo en el autocomplete
            if (this.data.node?.parentId && !this.data.parentNode) {
                this.loadNodeFromBackend(this.data.node.parentId, (parentNode) => {
                    this.data.parentNode = parentNode;
                    this.data.parentId = parentNode.id;
                    this.loadAvailableParents();
                });
            } else {
                this.loadAvailableParents();
            }
        }

        // Suscribirse a cambios en el campo de proyecto existente
        this.form.get('existingProjectId')?.valueChanges.pipe(takeUntil(this.destroying$)).subscribe((projectId: string) => {
            if (projectId) {
                this.onExistingProjectSelected(projectId);
            } else {
                this.selectedExistingProject = null;
                this.existingProjectInputControl.setValue('', { emitEvent: false });
            }
        });

        // Interceptar el cierre del diálogo (backdrop click) para verificar cambios sin guardar
        this.dialogRef.backdropClick().subscribe(() => {
            this.checkUnsavedChanges(() => {
                this.dialogRef.close();
            });
        });
    }

    save() {
        if (this.form.valid) {
            this.isLoading = true;
            
            const formValue = this.form.getRawValue();
            
            // Construir el código completo según el tipo de proyecto
            // Formato para guardar: Codigo Padre + "-" + Glosa/Segmento + "-" + Codigo de la lista
            // También guardar los valores por separado en campos distintos
            let codigo = formValue.codigo;
            let codigoPadre = '';
            let codigoGlosa = '';
            let codigoLista = '';
            
            // Determinar si tiene padre (por parentNode o parentId; en edición también por selección en tab Padre/Hijos)
            const hasParent = !(this.data as any).parentExplicitlyCleared &&
                ((this.isCreateMode && (this.data.parentNode || this.data.parentId)) ||
                 (this.isUpdateMode && (this.data.node?.parentId || this.data.parentId || this.data.parentNode)));
            
            // Obtener el código del padre
            const parentCode = this.data.parentNode?.code || this.parentCodePrefix || '';
            
            // Si tiene padre pero no tenemos el código del padre aún, intentar obtenerlo
            if (hasParent && !parentCode && this.data.parentId) {
                // Por ahora, usar el parentId como referencia
                // El backend debería poder manejar esto
            }
            
            if (hasParent && parentCode) {
                // Para tipo proyecto, codigoPadre debe ser el código completo del padre
                // que se usa como prefijo para construir el código del hijo
                codigoPadre = parentCode; // El código completo del padre (ej: "3318-ODS64-0")
                
                // Si parentCodePrefix no está establecido, establecerlo ahora
                if (!this.parentCodePrefix) {
                    this.parentCodePrefix = parentCode;
                }
                
                if (this.showLevelsDropdown) {
                    // Con lista de niveles: construir como padre-glosa-codigoLista
                    // Guardar valores por separado
                    codigoGlosa = formValue.codigo && formValue.codigo.trim() !== '' ? formValue.codigo : '';
                    codigoLista = formValue.codigoLista || '';
                    
                    // Si el usuario editó el código manualmente y ya tiene el formato correcto, usarlo
                    if (formValue.codigo && formValue.codigo.trim() !== '' && formValue.codigo.startsWith(parentCode)) {
                        // El usuario editó el código manualmente y ya incluye el padre
                        codigo = formValue.codigo;
                        // Extraer glosa y codigoLista del código editado
                        const remaining = codigo.substring(parentCode.length);
                        if (remaining.startsWith('-')) {
                            const parts = remaining.substring(1).split('-');
                            if (parts.length >= 2) {
                                codigoGlosa = parts[0] || '';
                                codigoLista = parts.slice(1).join('-');
                            } else if (parts.length === 1) {
                                codigoGlosa = parts[0] || '';
                            }
                        }
                    } else {
                        // Construir automáticamente: padre-glosa-codigoLista
                        // Si la glosa está vacía, el formato es: padre-codigoLista (sin guion extra)
                        const parts: string[] = [parentCode];
                        if (codigoGlosa && codigoGlosa.trim() !== '') {
                            parts.push(codigoGlosa);
                        }
                        if (codigoLista && codigoLista.trim() !== '') {
                            parts.push(codigoLista);
                        }
                        codigo = parts.join('-');
                    }
                } else {
                    // Para tipo administrativo u otros: prefijo heredado + sufijo editable
                    codigoGlosa = formValue.codigo || '';
                    codigo = `${parentCode}${formValue.codigo || ''}`;
                }
            } else if (hasParent && !parentCode) {
                // Si tiene padre pero no tenemos el código del padre, usar solo el código ingresado
                // Esto puede pasar si el parentNode no se cargó correctamente
                codigo = formValue.codigo || '';
                // Intentar establecer codigoPadre usando parentId si está disponible
                if (this.data.parentId) {
                    // El backend debería poder manejar esto con parent_id
                }
            }
            
            // Determinar parentId: puede venir de data.parentId o de data.parentNode.id
            // Si el usuario quitó el padre en el tab Padre/Hijos (parentExplicitlyCleared), enviar undefined
            const parentId = (this.data as any).parentExplicitlyCleared
                ? undefined
                : (this.data.parentId ||
                    (this.data.parentNode?.id ? this.data.parentNode.id.toString() : undefined) ||
                    (this.data.node?.parentId ? this.data.node.parentId : undefined));
            
            // Determinar level: si tiene padre y no se especificó level, calcularlo
            let projectLevel = this.data.level;
            if (!projectLevel && hasParent) {
                // Si tiene padre, el level debería ser el level del padre + 1
                if (this.data.parentNode?.level !== undefined) {
                    projectLevel = this.data.parentNode.level + 1;
                } else {
                    // Si no sabemos el level del padre, asumir que es 1 (hijo de raíz)
                    projectLevel = 1;
                }
            } else if (!projectLevel) {
                // Si no tiene padre, es nivel 0 (raíz)
                projectLevel = 0;
            }

            const parentTipo = this.resolveParentTipo();
            const tipoFinal =
                hasParent && parentTipo ? parentTipo : formValue.tipo;
            
            const formData: ProjectFormData = {
                id: this.data.node?.id,
                proyecto: formValue.proyecto,
                codigo: codigo, // Código completo (lo que se muestra en el label)
                // Guardar campos separados siempre (incluso si están vacíos) para poder recuperar el formulario
                codigoPadre: codigoPadre && codigoPadre.trim() !== '' ? codigoPadre : '', // Código del padre (solo para tipo proyecto)
                codigoGlosa: codigoGlosa && codigoGlosa.trim() !== '' ? codigoGlosa : '', // Glosa/segmento (solo para tipo proyecto)
                codigoLista: codigoLista && codigoLista.trim() !== '' ? codigoLista : '', // Código de la lista seleccionable (solo para tipo proyecto)
                pep: formValue.pep || '',
                aprobado: formValue.aprobado || false,
                activo: formValue.activo || false,
                visible: formValue.visible !== false,
                requiereObservacion: formValue.requiereObservacion || false,
                horasExtras: formValue.horasExtras || false,
                horasNormales: formValue.horasNormales !== false,
                tipo: tipoFinal,
                id_cliente: formValue.id_cliente,
                origen: formValue.origen,
                moneda: formValue.moneda,
                fechaInicio: formValue.fechaInicio || '',
                fechaTermino: formValue.fechaTermino || '',
                fechaEstimadaTermino: formValue.fechaEstimadaTermino || '',
                id_jefeProyecto: (formValue.id_jefeProyecto?.ID?.toString() || formValue.id_jefeProyecto || ''),
                id_controlProyecto: (formValue.id_controlProyecto?.ID?.toString() || formValue.id_controlProyecto || ''),
                id_controlDocumental: (formValue.id_controlDocumental?.ID?.toString() || formValue.id_controlDocumental || ''),
                HHEstimadas: formValue.HHEstimadas || null,
                parentId: parentId,
                level: projectLevel
            };

            // Validar datos antes de enviar
            if (!formData.proyecto || formData.proyecto.trim() === '') {
                this.snackbar.open('El nombre del proyecto es requerido', '', {
                    duration: 3000
                });
                this.isLoading = false;
                return;
            }
            
            if (!formData.codigo || formData.codigo.trim() === '') {
                this.snackbar.open('El código del proyecto es requerido', '', {
                    duration: 3000
                });
                this.isLoading = false;
                return;
            }
            
            // Si tiene padre pero no tiene codigoPadre, intentar obtenerlo del parentNode
            if (hasParent && !codigoPadre && this.data.parentNode?.code) {
                codigoPadre = this.data.parentNode.code;
                formData.codigoPadre = codigoPadre;
            }
            
            // Mapear a formato del backend
            const backendData = mapFrontendToBackend(formData);

            if (this.isCreateMode) {
                // Si se seleccionó un proyecto existente, actualizar su parent_id en lugar de crear uno nuevo
                if (this.selectedExistingProject && this.form.get('existingProjectId')?.value) {
                    // Actualizar el proyecto existente para asignarlo como hijo
                    const updatePayload = [{
                        ...backendData,
                        id: this.selectedExistingProject.id // Usar el ID del proyecto seleccionado
                    }];
                    
                    this.projectsService.update(updatePayload).subscribe({
                        next: (response) => {
                            this.isLoading = false;
                            const backendResponse = Array.isArray(response) ? response[0] : response;
                            const frontendData = mapBackendToFrontend(backendResponse);
                            this.form.markAsPristine();
                            this.handleSuccessResponse('Proyecto asignado como hijo correctamente');
                            this.dialogRef.close(frontendData);
                        },
                        error: (error) => {
                            this.isLoading = false;
                            console.error('Error al asignar proyecto como hijo:', error);
                            this.handleErrorResponse(error);
                        }
                    });
                } else {
                    // Crear nuevo proyecto
                    this.projectsService.create(backendData).subscribe({
                        next: (response) => {
                            this.isLoading = false;
                            // Mapear respuesta del backend al formato del frontend
                            const frontendData = mapBackendToFrontend(response);
                            // Marcar formulario como guardado
                            this.form.markAsPristine();
                            this.handleSuccessResponse('Nodo creado correctamente');
                            this.dialogRef.close(frontendData);
                        },
                        error: (error) => {
                            this.isLoading = false;
                            console.error('Error al crear proyecto:', error);
                            this.handleErrorResponse(error);
                        }
                    });
                }
            } else {
                // Actualizar proyecto existente
                // El backend espera un array en UpdateProjects
                
                // Asegurar que los campos estén presentes antes de crear el payload
                const payloadItem = {
                    ...backendData,
                    // Forzar parent_id null cuando el usuario quitó el padre en tab Padre/Hijos
                    ...((this.data as any).parentExplicitlyCleared ? { parent_id: null } : {}),
                    // Forzar que los campos de código separados estén presentes
                    codigo_padre: backendData.codigo_padre || '',
                    codigo_glosa: backendData.codigo_glosa || '',
                    codigo_lista: backendData.codigo_lista || ''
                };
                
                const updatePayload = [payloadItem];
                
                this.projectsService.update(updatePayload).subscribe({
                    next: (response) => {
                        this.isLoading = false;
                        // El backend devuelve el array, tomar el primer elemento
                        const backendResponse = Array.isArray(response) ? response[0] : response;
                        
                        const frontendData = mapBackendToFrontend(backendResponse);
                        
                        // Actualizar el nodo actual con los datos actualizados (incluyendo campos separados)
                        if (this.data.node) {
                            const updatedCode = frontendData.codigo || this.data.node.code;
                            this.data.node = {
                                ...this.data.node,
                                name: frontendData.proyecto || this.data.node.name,
                                code: updatedCode,
                                active: frontendData.activo !== false,
                                aprobado: frontendData.aprobado || false,
                                requiereObservacion: frontendData.requiereObservacion || false,
                                horasExtras: frontendData.horasExtras !== false,
                                horasNormales: frontendData.horasNormales !== false,
                                moneda: frontendData.moneda,
                                pep: frontendData.pep,
                                fechaInicio: frontendData.fechaInicio,
                                fechaTermino: frontendData.fechaTermino,
                                fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                                id_jefeProyecto: frontendData.id_jefeProyecto,
                                id_controlProyecto: frontendData.id_controlProyecto,
                                id_controlDocumental: frontendData.id_controlDocumental,
                                HHEstimadas: frontendData.HHEstimadas
                            };
                            (this.data.node as any).tipo = frontendData.tipo || '';
                            (this.data.node as any).id_cliente = frontendData.id_cliente || '';
                            (this.data.node as any).origen = frontendData.origen || '';
                            
                            // Actualizar los campos separados en el nodo (fuera del objeto para evitar problemas de tipo)
                            (this.data.node as any).codigoPadre = frontendData.codigoPadre;
                            (this.data.node as any).codigoGlosa = frontendData.codigoGlosa;
                            (this.data.node as any).codigoLista = frontendData.codigoLista;
                            
                            // Si usa lista de niveles, actualizar los campos del formulario con los valores guardados
                            // NO re-extraer del código completo, usar directamente los campos separados guardados
                            if (this.showLevelsDropdown) {
                                // Usar los campos separados que acabamos de guardar
                                // IMPORTANTE: Si frontendData no tiene los campos, usar los valores del payload original
                                const savedCodigoPadre = (frontendData.codigoPadre !== undefined && frontendData.codigoPadre !== null) 
                                    ? frontendData.codigoPadre 
                                    : (updatePayload[0]?.codigo_padre || '');
                                const savedCodigoGlosa = (frontendData.codigoGlosa !== undefined && frontendData.codigoGlosa !== null) 
                                    ? frontendData.codigoGlosa 
                                    : (updatePayload[0]?.codigo_glosa || '');
                                const savedCodigoLista = (frontendData.codigoLista !== undefined && frontendData.codigoLista !== null) 
                                    ? frontendData.codigoLista 
                                    : (updatePayload[0]?.codigo_lista || '');
                                
                                // IMPORTANTE: parentCodePrefix debe ser el código completo del padre
                                // Si codigo_padre guardado es solo "3318" pero el padre tiene código completo "3318-ODS64-0",
                                // usar el código completo del padre como prefijo
                                const parentCode = this.data.parentNode?.code || '';
                                
                                // Si el código del padre está disponible y es diferente del codigo_padre guardado,
                                // usar el código completo del padre (es más confiable)
                                if (parentCode) {
                                    this.parentCodePrefix = parentCode;
                                } else {
                                    // Si no hay código del padre disponible, usar el codigo_padre guardado
                                    this.parentCodePrefix = savedCodigoPadre;
                                }
                                
                                // Actualizar los valores internos
                                this.editableCodeSuffix = savedCodigoGlosa;
                                this.glosaCode = savedCodigoLista;
                                
                                // Actualizar el formulario directamente con los valores guardados
                                this.form.patchValue({
                                    codigo: savedCodigoGlosa,
                                    codigoLista: savedCodigoLista
                                }, { emitEvent: false });
                                
                                // Forzar actualización completa de la vista
                                this.cdr.detectChanges();
                                this.cdr.markForCheck();
                                
                                // Asegurar que los valores se reflejen en la vista
                                setTimeout(() => {
                                    this.cdr.detectChanges();
                                }, 0);
                            }
                            // NO continuar con la lógica de extracción, ya tenemos los valores correctos de los campos separados
                        }
                        
                        // Actualizar el título del diálogo si cambió el nombre
                        this.cdr.markForCheck();
                        
                        // Recargar hijos desde el backend para asegurar que se muestren los datos actualizados
                        // Usar setTimeout para asegurar que el backend haya procesado la actualización
                        setTimeout(() => {
                            this.loadChildrenFromBackend();
                        }, 100);
                        
                        // Marcar formulario como guardado
                        this.form.markAsPristine();
                        this.handleSuccessResponse('Nodo actualizado correctamente');
                        // No cerrar el diálogo en modo actualización - permanece abierto
                    },
                    error: (error) => {
                        this.isLoading = false;
                        console.error('Error al actualizar proyecto:', error);
                        this.handleErrorResponse(error);
                    }
                });
            }
        }
    }

    getDefaultType(): string {
        if (this.projectTypes.length === 0) return '';
        const defaultType = this.projectTypes.find(type => type.isDefault);
        return defaultType ? defaultType.id : (this.projectTypes[0]?.id || '');
    }

    getDefaultOrigin(): string {
        if (this.projectOrigins.length === 0) return '';
        const defaultOrigin = this.projectOrigins.find(origin => origin.isDefault);
        return defaultOrigin ? defaultOrigin.id : (this.projectOrigins[0]?.id || '');
    }

    getDefaultClient(): string {
        if (this.projectClients.length === 0) return '';
        return this.projectClients[0]?.id || '';
    }

    getDefaultCurrency(): string {
        if (this.projectCurrencies.length === 0) return '';
        const defaultCurrency = this.projectCurrencies.find(currency => currency.isDefault);
        return defaultCurrency ? defaultCurrency.id : (this.projectCurrencies[0]?.id || '');
    }

    cancel() {
        this.checkUnsavedChanges(() => {
            this.dialogRef.close();
        });
    }

    createSibling() {
        // Cambiado: ahora crea un hijo del nodo actual en vez de un hermano
        // Cerrar el diálogo actual y retornar información para crear un hijo
        // El componente padre detectará esto y abrirá un nuevo diálogo
        if (!this.data.node) {
            return;
        }
        
        // El nodo actual será el padre del nuevo hijo
        this.dialogRef.close({ 
            createSibling: true, 
            parentNode: this.data.node 
        });
    }

    get isCreateMode() {
        return this.mode === 'create';
    }

    get isUpdateMode() {
        return this.mode === 'update';
    }

    get isChildNode(): boolean {
        return this.data.level > 0 || !!this.data.parentNode || !!this.data.parentId || !!this.data.node?.parentId;
    }

    /**
     * Solo tipos Proyecto y ODS usan PEP (no administrativo ni cotización).
     */
    private tipoUsaPep(tipoId: string | undefined): boolean {
        if (!tipoId) return false;
        const t = this.projectTypes.find(x => x.id === tipoId);
        const raw = (t?.name || '').toLowerCase();
        const name = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (name.includes('administrativ')) return false;
        if (name.includes('cotiz')) return false;
        return name.includes('proyecto') || name.includes('ods');
    }

    /**
     * Tipo del padre (mismo que debe tener todo hijo en la jerarquía).
     */
    private resolveParentTipo(): string | undefined {
        if (this.data.parentNode?.tipo) {
            return String(this.data.parentNode.tipo);
        }
        const pid = this.data.parentId || this.data.node?.parentId;
        if (!pid || !this.data.allNodes?.length) {
            return undefined;
        }
        const idStr = String(pid);
        const parent = this.data.allNodes.find(n => n.id === idStr);
        return parent?.tipo ? String(parent.tipo) : undefined;
    }

    /**
     * Calcula el PEP heredado del padre directo.
     * Si el padre tiene PEP, lo usa directamente.
     * Si no, extrae del código: para "3318-5636" → "5636", para "3318-ODS100" → "3318" (código raíz).
     */
    private getInheritedPep(): string {
        if (!this.data.parentNode) return '';
        if (this.data.parentNode.tipo && !this.tipoUsaPep(this.data.parentNode.tipo)) {
            return '';
        }
        if (this.data.parentNode.pep) return this.data.parentNode.pep;
        
        const parentCode = this.data.parentNode.code || '';
        if (!parentCode) return '';
        
        const parts = parentCode.split('-');
        if (parts.length <= 1) return parentCode;
        
        const lastPart = parts[parts.length - 1];
        if (/^\d+$/.test(lastPart)) {
            return lastPart;
        }
        return parts[0];
    }

    /**
     * Auto-genera el nombre del proyecto al seleccionar un subcódigo.
     * Reglas:
     * - Si el padre tiene codigoLista (ya tiene subcódigo asignado):
     *   nombre = "[nombre padre] ([DESCRIPCIÓN SUBCÓDIGO])"
     * - Si el padre NO tiene codigoLista (nombre libre):
     *   nombre = "[nombre padre] | [descripción subcódigo]"
     */
    private autoGenerateNameFromSubcode(codigoLista: string): void {
        const selectedCode = this.availableCodes.find(c => c.code === codigoLista);
        if (!selectedCode) return;

        const parentName = this.data.parentNode?.name || '';
        if (!parentName) return;

        const subcodeDescription = selectedCode.description || '';
        if (!subcodeDescription) return;

        const parentHasSubcode = !!(this.data.parentNode?.codigoLista);

        let generatedName: string;
        if (parentHasSubcode) {
            generatedName = `${parentName} (${subcodeDescription.toUpperCase()})`;
        } else {
            generatedName = `${parentName} | ${subcodeDescription}`;
        }

        this.form.get('proyecto')?.setValue(generatedName);
    }

    get dialogTitle(): string {
        if (this.isCreateMode) {
            return this.data.level === 0 ? 'ACTIONS.CREAR_PROYECTO_RAIZ' : 'ACTIONS.CREAR_NODO_HIJO';
        }
        // En modo edición, mostrar el nombre del proyecto
        const projectName = this.data.node?.name || this.form?.get('proyecto')?.value || '';
        if (projectName) {
            return `ACTIONS.EDITAR_NODO - ${projectName}`;
        }
        return 'ACTIONS.EDITAR_NODO';
    }

    get currentProjectName(): string {
        // Priorizar el valor del formulario para que se actualice en tiempo real
        const formValue = this.form?.get('proyecto')?.value;
        return formValue || this.data.node?.name || '';
    }

    get levelDescription() {
        const levelNames = [
            'LEVELS.NIVEL_RAIZ',
            'LEVELS.NIVEL_1',
            'LEVELS.NIVEL_2',
            'LEVELS.NIVEL_3',
            'LEVELS.NIVEL_GENERICO'
        ];
        
        return levelNames[this.data.level] || `${levelNames[levelNames.length - 1]} ${this.data.level}`;
    }

    get visibleSiblingsColumns() {
        return this.siblingsColumns.filter(column => column.visible).map(column => column.property);
    }

    get hasSiblings(): boolean {
        // Cambiado: ahora verifica si tiene hijos en vez de hermanos
        if (!this.isUpdateMode) {
            return false;
        }
        if (!this.data.node || !this.data.node.id) {
            return false;
        }
        
        // Verificar primero el dataSource que es lo que realmente se muestra en la vista
        // Si el dataSource existe y tiene datos, retornar true
        if (this.siblingsDataSource) {
            const hasData = this.siblingsDataSource.data && this.siblingsDataSource.data.length > 0;
            if (hasData) {
                return true;
            }
            // Si el dataSource existe pero está vacío, significa que ya se cargó y no hay hijos
            // Retornar false para mostrar el botón de agregar hijo
            return false;
        }
        
        // Si no hay dataSource aún (no se ha cargado), verificar allNodes como fallback
        if (this.data.allNodes) {
            const children = this.getSiblings(); // Ahora retorna hijos
            return children && children.length > 0;
        }
        
        // Si no hay dataSource ni allNodes, asumir que no hay hijos (mostrar botón)
        return false;
    }

    get currentNodeId(): string {
        return this.data.node?.id || '';
    }

    /**
     * Verifica si el nodo actual tiene un padre (nivel > 0)
     */
    get hasParent(): boolean {
        if (!this.data.node) {
            return false;
        }
        // Tiene padre si level > 0 (nodos raíz tienen level 0)
        return this.data.node.level > 0;
    }

    /**
     * Navega al nodo padre cuando se está editando un hijo
     */
    goToParent(): void {
        if (!this.data.node || !this.data.node.parentId) {
            return;
        }

        // Verificar si hay cambios sin guardar antes de cambiar de registro
        this.checkUnsavedChanges(() => {
            this.performGoToParent();
        });
    }

    /**
     * Realiza la navegación al padre
     */
    private performGoToParent(): void {
        if (!this.data.node || !this.data.node.parentId) {
            return;
        }

        // SIEMPRE cargar desde el backend para obtener la información más actualizada
        // El callback cambiará al nodo padre una vez cargado
        this.loadNodeFromBackend(this.data.node.parentId, (parentNode) => {
            // El nodo padre ya está cargado desde el backend con todos sus campos actualizados
            // Ahora cambiar al nodo padre
        this.switchToNode(parentNode);
        });
    }

    /**
     * Carga un nodo desde el backend por su ID
     */
    private loadNodeFromBackend(nodeId: string, callback?: (node: ProjectTreeNode) => void): void {
        this.isLoading = true;
        this.projectsService.list('', 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }

                // Buscar el nodo por ID
                const project = projects.find((p: any) => {
                    const projectId = p.id?.toString() || p.ID?.toString() || '';
                    return projectId === nodeId;
                });

                if (!project) {
                    this.isLoading = false;
                    this.snackbar.open('No se encontró el nodo', 'Cerrar', {
                        duration: 3000
                    });
                    return;
                }

                // Mapear respuesta del backend al formato del frontend
                const frontendData = mapBackendToFrontend(project);

                const node: ProjectTreeNode = {
                    id: frontendData.id || project.id?.toString() || '',
                    name: frontendData.proyecto || project.name || '',
                    code: frontendData.codigo || project.code || '',
                    description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                    level: frontendData.level || project.level || 0,
                    parentId: frontendData.parentId || (project.parent_id ? project.parent_id.toString() : undefined),
                    expanded: false,
                    hasChildren: false,
                    children: [],
                    createdAt: project.created_at ? new Date(project.created_at) : new Date(),
                    updatedAt: project.updated_at ? new Date(project.updated_at) : undefined,
                    active: frontendData.activo !== false,
                    aprobado: frontendData.aprobado || false,
                    requiereObservacion: frontendData.requiereObservacion || false,
                    horasExtras: frontendData.horasExtras !== false,
                    horasNormales: frontendData.horasNormales !== false,
                    moneda: frontendData.moneda,
                    pep: frontendData.pep,
                    fechaInicio: frontendData.fechaInicio,
                    fechaTermino: frontendData.fechaTermino,
                    fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                    id_jefeProyecto: frontendData.id_jefeProyecto,
                    id_controlProyecto: frontendData.id_controlProyecto,
                    id_controlDocumental: frontendData.id_controlDocumental,
                    HHEstimadas: frontendData.HHEstimadas,
                    tipo: frontendData.tipo || project.tipo || project.Tipo || '',
                    id_cliente: frontendData.id_cliente || project.id_cliente || project.IdCliente || '',
                    origen: frontendData.origen || project.origen || project.Origen || '',
                    // Incluir campos de código separados
                    codigoPadre: frontendData.codigoPadre,
                    codigoGlosa: frontendData.codigoGlosa,
                    codigoLista: frontendData.codigoLista
                };

                // Actualizar en allNodes si existe
                if (this.data.allNodes) {
                    const existingIndex = this.data.allNodes.findIndex(n => n.id === node.id);
                    if (existingIndex >= 0) {
                        // Actualizar el nodo existente con los datos más recientes
                        this.data.allNodes[existingIndex] = node;
                    } else {
                        // Agregar si no existe
                        this.data.allNodes.push(node);
                    }
                } else {
                    this.data.allNodes = [node];
                }

                this.isLoading = false;
                
                // Ejecutar callback si se proporciona
                if (callback) {
                    callback(node);
                } else {
                    // Si no hay callback, asumir que es para cambiar al nodo
                    this.switchToNode(node);
                }
            },
            error: (err) => {
                this.isLoading = false;
                console.error('Error al cargar el nodo desde backend:', err);
                this.snackbar.open('Error al cargar el nodo', 'Cerrar', {
                    duration: 3000
                });
            }
        });
    }

    /**
     * Cambia el nodo actual al nodo especificado
     */
    private switchToNode(targetNode: ProjectTreeNode): void {
        // Actualizar el nodo actual
        this.data.node = {...targetNode};
        this.data.parentId = targetNode.parentId;
        this.data.level = targetNode.level;

        // Cargar el nodo padre desde el backend si existe (para asegurar datos actualizados)
        if (targetNode.parentId) {
            this.loadNodeFromBackend(targetNode.parentId, (parentNode) => {
                this.data.parentNode = parentNode;
                this.continueSwitchToNode(targetNode);
            });
            } else {
                this.data.parentNode = undefined;
            this.continueSwitchToNode(targetNode);
            }
        }

    /**
     * Continúa el proceso de cambio de nodo después de cargar el padre
     */
    private continueSwitchToNode(targetNode: ProjectTreeNode): void {

        // Separar código en prefijo y sufijo si tiene padre
        // Primero intentar usar los campos separados si están disponibles
        const nodeData = targetNode as any;
        const hasSeparateFields = nodeData.codigoPadre !== undefined || 
                                 nodeData.codigoGlosa !== undefined || 
                                 nodeData.codigoLista !== undefined;
        
        if (hasSeparateFields) {
            // Usar los campos separados directamente
            const savedCodigoPadre = nodeData.codigoPadre !== undefined ? (nodeData.codigoPadre || '') : '';
            const parentNodeCode = this.data.parentNode?.code || '';
            
            // SIEMPRE usar el código completo del padre como prefijo si está disponible
            if (parentNodeCode) {
                this.parentCodePrefix = parentNodeCode;
            } else {
                this.parentCodePrefix = savedCodigoPadre;
            }
            
            this.editableCodeSuffix = nodeData.codigoGlosa !== undefined ? (nodeData.codigoGlosa || '') : '';
            this.glosaCode = nodeData.codigoLista !== undefined ? (nodeData.codigoLista || '') : '';
        } else if (targetNode.parentId && targetNode.code) {
            // Si no hay campos separados, extraer del código completo (compatibilidad con datos antiguos)
            let parentCode: string | null = null;
            if (this.data.parentNode?.code) {
                parentCode = this.data.parentNode.code;
            } else if (this.data.allNodes) {
                parentCode = this.getParentCodeForNode(targetNode);
            }
            
            if (parentCode) {
                const codeParts = this.splitCode(targetNode.code, parentCode);
                this.parentCodePrefix = codeParts.prefix;
                this.editableCodeSuffix = codeParts.suffix;
            } else {
                this.parentCodePrefix = '';
                this.editableCodeSuffix = targetNode.code;
            }
        } else {
            this.parentCodePrefix = '';
            this.editableCodeSuffix = targetNode.code || '';
        }

        // Recargar la lista desplegable por nivel cuando se cambia al nodo padre
        // Esto es necesario porque el nivel puede haber cambiado
        const currentTipoId = (targetNode as any).tipo || targetNode.tipo || '';
        if (currentTipoId) {
            // Recargar códigos según el nuevo nivel
            // Usar setTimeout para asegurar que el nivel se haya actualizado
            setTimeout(() => {
                this.updateCodeFieldBasedOnType(currentTipoId);
            }, 0);
        }

        // Actualizar el formulario con los datos del nuevo nodo
        this.form.patchValue({
            proyecto: targetNode.name || '',
            codigo: this.editableCodeSuffix,
            pep: targetNode.pep || '',
            aprobado: targetNode.aprobado || false,
            activo: targetNode.active !== false,
            requiereObservacion: targetNode.requiereObservacion || false,
            horasExtras: targetNode.horasExtras || false,
            horasNormales: targetNode.horasNormales !== false,
            tipo: targetNode.tipo || '',
            id_cliente: targetNode.id_cliente || '',
            origen: targetNode.origen || '',
            moneda: targetNode.moneda || '',
            fechaInicio: targetNode.fechaInicio || '',
            fechaTermino: targetNode.fechaTermino || '',
            fechaEstimadaTermino: targetNode.fechaEstimadaTermino || '',
            id_jefeProyecto: this.getUserObjectById(targetNode.id_jefeProyecto),
            id_controlProyecto: this.getUserObjectById(targetNode.id_controlProyecto),
            id_controlDocumental: this.getUserObjectById(targetNode.id_controlDocumental),
            HHEstimadas: targetNode.HHEstimadas || null,
            codigoLista: this.glosaCode || '' // Actualizar también el campo codigoLista si hay campos separados
        });

        // Recargar hijos del nuevo nodo
        this.loadChildrenFromBackend();

        // Marcar formulario como no modificado ya que cambiamos de registro
        this.form.markAsPristine();
    }

    ngAfterViewInit(): void {
        // Configurar paginador y ordenamiento después de que la vista se inicialice
        if (this.isUpdateMode && this.siblingsDataSource) {
            // Esperar a que la vista se renderice completamente
            setTimeout(() => {
                if (this.siblingsPaginator) {
                    this.siblingsDataSource.paginator = this.siblingsPaginator;
                }
                if (this.siblingsSort) {
                    this.siblingsDataSource.sort = this.siblingsSort;
                }
                // Forzar detección de cambios
                this.cdr.detectChanges();
            }, 0);
        }
    }

    editSibling(siblingNode: ProjectTreeNode) {
        // Evitar editar el mismo nodo
        if (siblingNode.id === this.data.node?.id) {
            return;
        }

        // Verificar si hay cambios sin guardar antes de cambiar de registro
        this.checkUnsavedChanges(() => {
            // SIEMPRE cargar desde el backend para obtener la información más actualizada
            this.loadNodeFromBackend(siblingNode.id, (loadedNode) => {
                this.performEditSibling(loadedNode);
            });
        });
    }

    private performEditSibling(siblingNode: ProjectTreeNode) {
        // Actualizar el nodo actual
        this.data.node = {...siblingNode}; // Crear una copia para forzar detección de cambios
        this.data.parentId = siblingNode.parentId;
        this.data.level = siblingNode.level;

        // Cargar el nodo padre desde el backend si existe (para asegurar datos actualizados)
        if (siblingNode.parentId) {
            this.loadNodeFromBackend(siblingNode.parentId, (parentNode) => {
                this.data.parentNode = parentNode;
                this.continueEditSibling(siblingNode);
            });
        } else {
            this.data.parentNode = undefined;
            this.continueEditSibling(siblingNode);
            }
        }

    /**
     * Continúa el proceso de edición del hermano después de cargar el padre
     */
    private continueEditSibling(siblingNode: ProjectTreeNode): void {
        
        // Separar código en prefijo y sufijo si tiene padre
        // Primero intentar usar los campos separados si están disponibles
        const nodeData = siblingNode as any;
        const hasSeparateFields = nodeData.codigoPadre !== undefined || 
                                 nodeData.codigoGlosa !== undefined || 
                                 nodeData.codigoLista !== undefined;
        
        if (hasSeparateFields) {
            // Usar los campos separados directamente
            const savedCodigoPadre = nodeData.codigoPadre !== undefined ? (nodeData.codigoPadre || '') : '';
            const parentNodeCode = this.data.parentNode?.code || '';
            
            // SIEMPRE usar el código completo del padre como prefijo si está disponible
            if (parentNodeCode) {
                this.parentCodePrefix = parentNodeCode;
            } else {
                this.parentCodePrefix = savedCodigoPadre;
            }
            
            this.editableCodeSuffix = nodeData.codigoGlosa !== undefined ? (nodeData.codigoGlosa || '') : '';
            this.glosaCode = nodeData.codigoLista !== undefined ? (nodeData.codigoLista || '') : '';
        } else if (siblingNode.parentId && siblingNode.code) {
            // Si no hay campos separados, extraer del código completo (compatibilidad con datos antiguos)
            let parentCode: string | null = null;
            if (this.data.parentNode?.code) {
                parentCode = this.data.parentNode.code;
            } else {
                parentCode = this.getParentCodeForNode(siblingNode);
            }
            
            if (parentCode) {
                const codeParts = this.splitCode(siblingNode.code, parentCode);
                this.parentCodePrefix = codeParts.prefix;
                this.editableCodeSuffix = codeParts.suffix;
            } else {
                this.parentCodePrefix = '';
                this.editableCodeSuffix = siblingNode.code;
            }
        } else {
            this.parentCodePrefix = '';
            this.editableCodeSuffix = siblingNode.code || '';
        }
        
        // Recargar la lista desplegable por nivel cuando se cambia al nodo hijo
        // Esto es necesario porque el nivel puede haber cambiado
        const currentTipoId = (siblingNode as any).tipo || siblingNode.tipo || '';
        if (currentTipoId) {
            // Recargar códigos según el nuevo nivel
            // Usar setTimeout para asegurar que el nivel se haya actualizado
            setTimeout(() => {
                this.updateCodeFieldBasedOnType(currentTipoId);
            }, 0);
        }
        
        // Actualizar el formulario con los datos del nuevo nodo
        this.form.patchValue({
            proyecto: siblingNode.name || '',
            codigo: this.editableCodeSuffix, // Solo la parte editable (glosa)
            codigoLista: this.glosaCode || '', // Código de la lista seleccionable
            pep: siblingNode.pep || '',
            aprobado: siblingNode.aprobado || false,
            activo: siblingNode.active !== false,
            requiereObservacion: siblingNode.requiereObservacion || false,
            horasExtras: siblingNode.horasExtras || false,
            horasNormales: siblingNode.horasNormales !== false,
            tipo: siblingNode.tipo || '',
            id_cliente: siblingNode.id_cliente || '',
            origen: siblingNode.origen || '',
            moneda: siblingNode.moneda || '',
            fechaInicio: siblingNode.fechaInicio || '',
            fechaTermino: siblingNode.fechaTermino || '',
            fechaEstimadaTermino: siblingNode.fechaEstimadaTermino || '',
            id_jefeProyecto: this.getUserObjectById(siblingNode.id_jefeProyecto),
            id_controlProyecto: this.getUserObjectById(siblingNode.id_controlProyecto),
            id_controlDocumental: this.getUserObjectById(siblingNode.id_controlDocumental),
            HHEstimadas: siblingNode.HHEstimadas || null
        });

        // Marcar el formulario como "pristine" después de cargar los datos
        // para que no se detecte como cambio sin guardar
        this.form.markAsPristine();

        // Recargar hijos desde el backend para asegurar que se muestren solo los hijos del nodo actual
        this.loadChildrenFromBackend();
        
        // Reasignar paginador y sort después de que la vista se actualice
        // Usar requestAnimationFrame para asegurar que se ejecute después del render
        requestAnimationFrame(() => {
            if (this.siblingsDataSource) {
                if (this.siblingsPaginator) {
                    this.siblingsDataSource.paginator = this.siblingsPaginator;
                    if (this.siblingsPaginator.pageIndex !== 0) {
                        this.siblingsPaginator.firstPage();
                    }
                }
                if (this.siblingsSort) {
                    this.siblingsDataSource.sort = this.siblingsSort;
                }
            }
            // Forzar detección de cambios final
            this.cdr.detectChanges();
        });
    }

    isCurrentNode(nodeId: string): boolean {
        return nodeId === this.currentNodeId;
    }

    /**
     * Carga los hijos del nodo actual desde el backend
     */
    private loadChildrenFromBackend(): void {
        if (!this.data.node || !this.data.node.id) {
            this.initializeSiblingsGrid();
            return;
        }

        // Usar el ID del nodo actual para buscar los hijos
        const currentNodeId = this.data.node.id;
        
        // Buscar proyectos cuyo parent_id sea el id del nodo actual
        // El backend espera el campo parentID en el struct SearchProject
        const searchObj = { 
            parentID: currentNodeId
        };
        const searchString = JSON.stringify(searchObj);

        this.projectsService.list(searchString, 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }

                // Filtrar SOLO los hijos directos del nodo actual (por si el backend no filtra correctamente)
                const childrenProjects = projects.filter((project: any) => {
                    const projectParentId = project.parent_id?.toString() || 
                                          project.ParentID?.toString() || 
                                          project.parentId?.toString() || 
                                          project.ParentId?.toString();
                    return projectParentId === currentNodeId;
                });

                // Mapear los proyectos filtrados a ProjectTreeNode
                const children = childrenProjects.map((project: any) => {
                    const frontendData = mapBackendToFrontend(project);
                    return {
                        id: frontendData.id || project.id?.toString() || '',
                        name: frontendData.proyecto || project.name || '',
                        code: frontendData.codigo || project.code || '',
                        description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                        level: frontendData.level || project.level || 0,
                        parentId: frontendData.parentId || (project.parent_id ? project.parent_id.toString() : undefined),
                        expanded: false,
                        hasChildren: false,
                        children: [],
                        createdAt: project.created_at ? new Date(project.created_at) : new Date(),
                        updatedAt: project.updated_at ? new Date(project.updated_at) : undefined,
                        active: frontendData.activo !== false,
                        aprobado: frontendData.aprobado || false,
                        requiereObservacion: frontendData.requiereObservacion || false,
                        horasExtras: frontendData.horasExtras !== false,
                        horasNormales: frontendData.horasNormales !== false,
                        moneda: frontendData.moneda,
                        pep: frontendData.pep,
                        fechaInicio: frontendData.fechaInicio,
                        fechaTermino: frontendData.fechaTermino,
                        fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                        id_jefeProyecto: frontendData.id_jefeProyecto,
                        id_controlProyecto: frontendData.id_controlProyecto,
                        id_controlDocumental: frontendData.id_controlDocumental,
                        HHEstimadas: frontendData.HHEstimadas,
                        tipo: frontendData.tipo || project.tipo || project.Tipo || '',
                        id_cliente: frontendData.id_cliente || project.id_cliente || project.IdCliente || '',
                        origen: frontendData.origen || project.origen || project.Origen || ''
                    } as ProjectTreeNode;
                });

                // Actualizar allNodes con los hijos encontrados
                if (this.data.allNodes) {
                    // Eliminar hijos antiguos del nodo actual y agregar los nuevos
                    this.data.allNodes = this.data.allNodes.filter(n => n.parentId !== currentNodeId);
                    this.data.allNodes.push(...children);
                } else {
                    this.data.allNodes = children;
                }

                // Inicializar la grilla directamente con los hijos cargados
                this.siblingsDataSource = new MatTableDataSource<ProjectTreeNode>(children);
                this.siblingsTableKey++;
                
                // Aplicar filtro si existe
                this.applyChildrenFilter();
                
                // Configurar paginador y ordenamiento si están disponibles
                setTimeout(() => {
                    if (this.siblingsPaginator) {
                        this.siblingsDataSource.paginator = this.siblingsPaginator;
                    }
                    if (this.siblingsSort) {
                        this.siblingsDataSource.sort = this.siblingsSort;
                    }
                    this.cdr.detectChanges();
                }, 0);
            },
            error: (err) => {
                console.error('Error al cargar hijos desde el backend:', err);
                // En caso de error, usar los datos locales
                this.initializeSiblingsGrid();
            }
        });
    }

    private initializeSiblingsGrid() {
        // Solo mostrar hijos del nodo actual, no todos los registros
        if (!this.data.node || !this.data.node.id) {
            this.siblingsDataSource = new MatTableDataSource<ProjectTreeNode>([]);
            this.siblingsTableKey++;
            return;
        }

        const siblings = this.getSiblings();
        // Crear nueva instancia del dataSource solo con los hijos
        this.siblingsDataSource = new MatTableDataSource<ProjectTreeNode>(siblings);
        // Incrementar clave de tabla
        this.siblingsTableKey++;
        
        // Configurar paginador y ordenamiento si están disponibles
        setTimeout(() => {
            if (this.siblingsPaginator) {
                this.siblingsDataSource.paginator = this.siblingsPaginator;
            }
            if (this.siblingsSort) {
                this.siblingsDataSource.sort = this.siblingsSort;
            }
            this.cdr.detectChanges();
        }, 0);
    }

    trackByNodeId(index: number, node: ProjectTreeNode): string {
        return node.id;
    }

    private getSiblings(): ProjectTreeNode[] {
        // Retorna SOLO los hijos directos del nodo actual (nodos cuyo parentId es el id del nodo actual)
        if (!this.data.node || !this.data.node.id) {
            return [];
        }

        const currentNodeId = this.data.node.id;

        // Si no hay allNodes, retornar array vacío
        if (!this.data.allNodes || this.data.allNodes.length === 0) {
            return [];
        }

        // Filtrar SOLO los hijos directos del nodo actual
        // Un hijo es un nodo cuyo parentId coincide exactamente con el id del nodo actual
        return this.data.allNodes.filter(node => 
            node.parentId === currentNodeId
        );
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    /**
     * Configura el filtro para los hijos
     */
    private setupChildrenFilter(): void {
        // Suscribirse a cambios en el filtro para aplicarlo a la tabla
        this.childrenFilterControl.valueChanges.pipe(
            takeUntil(this.destroying$)
        ).subscribe(() => {
            this.applyChildrenFilter();
        });
    }

    /**
     * Aplica el filtro a la tabla de hijos
     */
    private applyChildrenFilter(): void {
        if (!this.siblingsDataSource) {
            return;
        }
        
        // Configurar el filtro personalizado
        this.siblingsDataSource.filterPredicate = (data: ProjectTreeNode, filter: string) => {
            if (!filter) {
                return true;
            }
            const searchStr = filter.toLowerCase();
            const code = (data.code || '').toLowerCase();
            const description = (data.description || '').toLowerCase();
            const name = (data.name || '').toLowerCase();
            return code.includes(searchStr) || description.includes(searchStr) || name.includes(searchStr);
        };
        
        // Aplicar el filtro actual
        const filterValue = this.childrenFilterControl.value || '';
        this.siblingsDataSource.filter = filterValue;
    }
    
    /**
     * Se ejecuta cuando cambia el valor del filtro de hijos
     */
    onChildrenFilterChange(): void {
        this.applyChildrenFilter();
    }

    /**
     * Genera el código del hijo basándose en el código del padre y el número de hermanos existentes
     * Formato: códigoPadre-001, códigoPadre-002, etc.
     */
    private generateChildCode(): string {
        if (!this.data.parentNode || !this.data.parentNode.code) {
            // Si no hay código del padre, generar uno base
            return '';
        }

        const parentCode = this.data.parentNode.code;
        const parentId = this.data.parentNode.id;

        // Contar los hermanos existentes (hijos del mismo padre)
        let siblingCount = 0;
        if (this.data.allNodes && this.data.allNodes.length > 0) {
            siblingCount = this.data.allNodes.filter(node => 
                node.parentId === parentId
            ).length;
        }

        // Generar el siguiente número secuencial (001, 002, 003, etc.)
        const nextNumber = (siblingCount + 1).toString().padStart(3, '0');
        
        // Retornar el código del padre + "-" + número secuencial
        return `${parentCode}-${nextNumber}`;
    }

    /**
     * Separa el código completo en prefijo (heredado del padre) y sufijo (editable)
     * Ejemplo: "PROY-001-002" con padre "PROY-001" -> prefix: "PROY-001-", suffix: "002"
     */
    private splitCode(fullCode: string, parentCode: string): { prefix: string; suffix: string } {
        if (!parentCode || !fullCode) {
            return { prefix: '', suffix: fullCode || '' };
        }

        // El prefijo debe ser el código del padre + "-"
        const expectedPrefix = `${parentCode}-`;
        
        if (fullCode.startsWith(expectedPrefix)) {
            const suffix = fullCode.substring(expectedPrefix.length);
            return {
                prefix: expectedPrefix,
                suffix: suffix || ''
            };
        }

        // Si el código no comienza con el prefijo esperado, buscar el último segmento
        // Esto maneja casos donde el código puede tener una estructura diferente
        const lastDashIndex = fullCode.lastIndexOf('-');
        if (lastDashIndex > 0) {
            const potentialPrefix = fullCode.substring(0, lastDashIndex + 1);
            // Verificar si el prefijo potencial contiene el código del padre
            if (potentialPrefix.startsWith(parentCode) && potentialPrefix.endsWith('-')) {
                return {
                    prefix: potentialPrefix,
                    suffix: fullCode.substring(lastDashIndex + 1)
                };
            }
        }

        // Si no se puede separar, retornar todo como editable
        return { prefix: '', suffix: fullCode };
    }

    /**
     * Obtiene el código del padre para un nodo dado
     */
    private getParentCodeForNode(node: ProjectTreeNode): string | null {
        if (!node.parentId || !this.data.allNodes) {
            return null;
        }

        const parent = this.data.allNodes.find(n => n.id === node.parentId);
        return parent?.code || null;
    }

    /**
     * Verifica si hay un prefijo heredado (si es un hijo)
     */
    get hasInheritedCode(): boolean {
        return !!this.parentCodePrefix;
    }

    /**
     * Maneja respuestas exitosas
     */
    private handleSuccessResponse(message?: string): void {
        const defaultMessage = message || (this.isCreateMode ? 'Nodo creado correctamente' : 'Nodo actualizado correctamente');
        this.snackbar.open(defaultMessage, '', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top'
        });
    }

    /**
     * Maneja errores de la API
     */
    private handleErrorResponse(error: any): void {
        let errorMessage = 'Error al procesar la solicitud';
        
        if (error?.error?.message) {
            errorMessage = error.error.message;
        } else if (error?.message) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        }

        this.snackbar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
        });
    }

    /**
     * Verifica si hay cambios sin guardar y muestra un diálogo de confirmación
     * @param onConfirm Callback a ejecutar si el usuario confirma
     */
    private checkUnsavedChanges(onConfirm: () => void): void {
        // Verificar si el formulario tiene cambios sin guardar
        if (this.form && this.form.dirty) {
            const dialogData = {
                title: 'Cambios sin guardar',
                message: 'Tienes cambios sin guardar. ¿Estás seguro de que deseas continuar? Se perderán todos los cambios no guardados.',
                confirmText: 'Continuar',
                cancelText: 'Cancelar'
            };

            const confirmDialog = this.dialog.open(DialogComponent, {
                data: dialogData,
                disableClose: true,
                width: '400px'
            });

            confirmDialog.afterClosed().subscribe((result) => {
                if (result) {
                    // Si el usuario confirma, ejecutar la acción
                    onConfirm();
                }
            });
        } else {
            // Si no hay cambios, ejecutar directamente
            onConfirm();
        }
    }

    ngOnDestroy(): void {
        this.destroying$.next();
        this.destroying$.complete();
    }

    /**
     * Carga los datos de los mantenedores desde los servicios
     */
    private loadMaintainerData(): void {
        // Cargar Tipos de Proyecto
        this.projectTypesService.list('', 0, 1000, 'name ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const types = data?.data || data?.Data || (Array.isArray(data) ? data : []);
                this.projectTypes = types
                    .filter((item: any) => item && item.active !== false)
                    .map((item: any) => {
                        const mapped: ProjectType = {
                            id: item.id?.toString() || item.ID?.toString() || '',
                            name: item.name || ''
                        };
                        if (item.mostrarListaNiveles !== undefined && item.mostrarListaNiveles !== null) {
                            mapped.mostrarListaNiveles = item.mostrarListaNiveles === true;
                        }
                        return mapped;
                    });
                
                // Después de cargar tipos, si hay un tipo seleccionado, cargar los códigos del nivel correspondiente
                const currentTipoId = this.form?.get('tipo')?.value || this.data.node?.tipo;
                if (currentTipoId) {
                    // Cargar códigos según el tipo seleccionado y el nivel
                    this.updateCodeFieldBasedOnType(currentTipoId);
                    
                    // Si estamos en modo edición y es tipo proyecto, verificar si necesitamos extraer el código
                    // Solo extraer si NO tenemos campos separados disponibles
                    if (this.isUpdateMode && this.data.node?.tipo && this.data.node?.code) {
                        const nodeData = this.data.node as any;
                        const hasSeparateFields = nodeData.codigoPadre !== undefined || 
                                                 nodeData.codigoGlosa !== undefined || 
                                                 nodeData.codigoLista !== undefined;
                        
                        // Si ya tenemos campos separados, no necesitamos extraer del código completo
                        if (!hasSeparateFields) {
                            const tipo = this.projectTypes.find(t => t.id === currentTipoId);
                            if (this.typeUsesLevelCodes(tipo) && this.data.parentNode?.code && this.data.node?.code) {
                                // Para tipo proyecto, el código es: padre-glosa-codigoLista
                                // Para niveles > 1, el código del padre ya es completo (ej: "3318-ODS64-0")
                                // Necesitamos extraer correctamente: glosa y codigoLista del nivel actual
                                const parentCode = this.data.parentNode.code;
                                const fullCode = this.data.node.code;
                                
                                if (fullCode.startsWith(parentCode)) {
                                    const remaining = fullCode.substring(parentCode.length);
                                    
                                    // Si el código tiene formato con guiones (padre-glosa-codigoLista)
                                    if (remaining.startsWith('-')) {
                                        // Esperar a que los códigos se carguen para identificar correctamente el código de la lista
                                        // Esto es especialmente importante para niveles > 1
                                        // Usar un timeout más largo para asegurar que los códigos estén cargados
                                        setTimeout(() => {
                                            if (this.availableCodes.length > 0) {
                                                this.extractCodeForProjectTypeWithCodes();
                                            } else {
                                                // Si aún no hay códigos, intentar de nuevo
                                                setTimeout(() => {
                                                    this.extractCodeForProjectTypeWithCodes();
                                                }, 200);
                                            }
                                        }, 300);
                                    } else {
                                        // Formato antiguo - esperar a que los códigos se carguen
                                        setTimeout(() => {
                                            this.extractCodeForProjectType();
                                        }, 200);
                                    }
                                }
                            }
                        } else {
                            // Ya tenemos campos separados, solo actualizar el formulario con esos valores
                            // Asegurar que parentCodePrefix use el código completo del padre
                            const parentNodeCode = this.data.parentNode?.code || '';
                            if (parentNodeCode) {
                                this.parentCodePrefix = parentNodeCode;
                            } else {
                                const savedCodigoPadre = nodeData.codigoPadre !== undefined ? (nodeData.codigoPadre || '') : '';
                                this.parentCodePrefix = savedCodigoPadre;
                            }
                            
                            // Actualizar los valores internos
                            this.editableCodeSuffix = nodeData.codigoGlosa !== undefined ? (nodeData.codigoGlosa || '') : '';
                            this.glosaCode = nodeData.codigoLista !== undefined ? (nodeData.codigoLista || '') : '';
                            
                            // Asegurar que el formulario tenga los valores correctos
                            if (this.form) {
                                this.form.patchValue({
                                    codigo: this.editableCodeSuffix,
                                    codigoLista: this.glosaCode
                                }, { emitEvent: false });
                                this.cdr.markForCheck();
                            }
                        }
                    }
                }
            },
            error: (err) => {
                console.error('Error loading project types:', err);
                this.projectTypes = [];
            }
        });

        // Cargar Orígenes
        this.originsService.list('', 0, 1000, 'name ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const origins = data?.data || data?.Data || (Array.isArray(data) ? data : []);
                this.projectOrigins = origins
                    .filter((item: any) => item && item.active !== false)
                    .map((item: any) => ({
                        id: item.id?.toString() || item.ID?.toString() || '',
                        name: item.name || ''
                    }));
            },
            error: (err) => {
                console.error('Error loading origins:', err);
                this.projectOrigins = [];
            }
        });

        // Cargar Clientes
        this.clientsService.list('', 0, 1000, 'name ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const clients = data?.data || data?.Data || (Array.isArray(data) ? data : []);
                this.projectClients = clients
                    .filter((item: any) => item && item.active !== false)
                    .map((item: any) => ({
                        id: item.id?.toString() || item.ID?.toString() || '',
                        name: item.name || '',
                        empresa: item.name || ''
                    }));
            },
            error: (err) => {
                console.error('Error loading clients:', err);
                this.projectClients = [];
            }
        });

        // Cargar Monedas
        this.currenciesService.list('', 0, 1000, 'name ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const currencies = data?.data || data?.Data || (Array.isArray(data) ? data : []);
                this.projectCurrencies = currencies
                    .filter((item: any) => item && item.active !== false)
                    .map((item: any) => ({
                        id: item.id?.toString() || item.ID?.toString() || '',
                        code: item.code || '',
                        name: item.name || '',
                        symbol: item.symbol || ''
                    }));
            },
            error: (err) => {
                console.error('Error loading currencies:', err);
                this.projectCurrencies = [];
            }
        });

        // Cargar Usuarios
        this.usersService.list('', 0, 10000, '', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const users = data?.data || data?.Data || (Array.isArray(data) ? data : []);
                this.userListAuto = users
                    .filter((user: any) => user && user.active !== false)
                    .map((user: any) => {
                        const userObj: User = {
                            ...user,
                            name: (user.lastName || '') + ' ' + (user.firstName || '')
                        };
                        return userObj;
                    });

                // Inicializar observables filtrados después de cargar usuarios
                this.initializeUserAutocompletes();
                
                // Si estamos en modo edición, establecer los valores iniciales de los usuarios
                if (this.isUpdateMode && this.data.node) {
                    this.setInitialUserValues();
                }
            },
            error: (err) => {
                console.error('Error loading users:', err);
                this.userListAuto = [];
            }
        });
    }

    /**
     * Cargar códigos disponibles según el nivel seleccionado
     * @param level Nivel del cual cargar los códigos
     * @param parentCodeId ID del código padre para filtrar (opcional)
     */
    private loadCodesByLevel(level: number, parentCodeId?: number): void {
        if (!level || level < 1) {
            this.availableCodes = [];
            this.cdr.markForCheck();
            return;
        }

        this.levelsService.getByLevel(level).pipe(takeUntil(this.destroying$)).subscribe({
            next: (codes: Level[]) => {
                let allCodes = codes || [];
                
                // Si se proporciona parentCodeId, filtrar códigos que tengan ese parent_id
                if (parentCodeId !== undefined && parentCodeId !== null) {
                    allCodes = allCodes.filter(c => {
                        const codeParentId = c.parent_id || c.parent?.id;
                        return codeParentId === parentCodeId;
                    });
                }
                
                // Si hay prefijo heredado, intentar filtrar códigos que empiecen con el prefijo
                // Si no hay coincidencias, usar todos los códigos (pueden ser códigos simples)
                if (this.hasInheritedCode && this.parentCodePrefix) {
                    const filteredCodes = allCodes.filter(c => c.code && c.code.startsWith(this.parentCodePrefix));
                    if (filteredCodes.length > 0) {
                        // Hay códigos que empiezan con el prefijo: extraer solo el sufijo
                        this.availableCodes = filteredCodes.map(c => ({
                            ...c,
                            code: c.code.substring(this.parentCodePrefix.length) // Solo el sufijo
                        }));
                    } else {
                        // No hay códigos con el prefijo: usar todos los códigos (son códigos simples)
                        this.availableCodes = allCodes;
                    }
                } else {
                    // Sin prefijo: usar los códigos completos
                    this.availableCodes = allCodes;
                }
                
                // Forzar detección de cambios para actualizar la vista
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Error loading codes by level:', err);
                this.availableCodes = [];
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Verifica si el tipo de proyecto es "administrativo"
     */
    get isAdministrativeType(): boolean {
        const tipoId = this.form?.get('tipo')?.value;
        if (!tipoId) return false;
        
        const tipo = this.projectTypes.find(t => t.id === tipoId);
        if (!tipo) return false;
        
        const tipoName = tipo.name?.toLowerCase() || '';
        return tipoName.includes('administrativo');
    }

    /**
     * Verifica si el tipo de proyecto es "proyecto"
     */
    get isProjectType(): boolean {
        const tipoId = this.form?.get('tipo')?.value;
        if (!tipoId) return false;
        
        const tipo = this.projectTypes.find(t => t.id === tipoId);
        if (!tipo) return false;
        
        const tipoName = tipo.name?.toLowerCase() || '';
        return tipoName.includes('proyecto') && !tipoName.includes('administrativo');
    }

    /**
     * Indica si debe mostrarse el dropdown de niveles (subcódigos) y usarse la lógica padre-glosa-código lista.
     * Prioridad: flag en tipo de proyecto; si no viene del API, mismo criterio histórico por nombre (proyecto / no administrativo).
     */
    private typeUsesLevelCodes(tipo: ProjectType | undefined): boolean {
        if (!tipo) return false;
        if (tipo.mostrarListaNiveles === true) return true;
        if (tipo.mostrarListaNiveles === false) return false;
        const tipoName = tipo.name?.toLowerCase() || '';
        return tipoName.includes('proyecto') && !tipoName.includes('administrativo');
    }

    get showLevelsDropdown(): boolean {
        const tipoId = this.form?.get('tipo')?.value;
        if (!tipoId) return false;
        const tipo = this.projectTypes.find(t => t.id === tipoId);
        return this.typeUsesLevelCodes(tipo);
    }

    /**
     * Actualiza el campo código según el tipo de proyecto seleccionado
     */
    private updateCodeFieldBasedOnType(tipoId: string): void {
        if (!tipoId) {
            this.availableCodes = [];
            this.cdr.markForCheck();
            return;
        }

        const tipo = this.projectTypes.find(t => t.id === tipoId);
        if (!tipo) {
            this.availableCodes = [];
            this.cdr.markForCheck();
            return;
        }

        const tipoName = tipo.name?.toLowerCase() || '';
        const isAdministrative = tipoName.includes('administrativo');
        const useLevelCodes = this.typeUsesLevelCodes(tipo);

        // Solo cargar códigos si el nivel es mayor a 0 (nodos raíz no necesitan códigos)
        if (this.data.level === 0) {
            this.availableCodes = [];
            this.cdr.markForCheck();
            return;
        }

        // Si el tipo usa lista de niveles y es hijo (tiene código padre o parentId), cargar códigos según el nivel del PADRE
        // Funciona para todos los niveles (1, 2, 3, 4, etc.)
        // Verificar si tiene padre por parentId o por parentCodePrefix
        const hasParent = this.hasInheritedCode || (this.data.parentId || this.data.parentNode);
        
        if (useLevelCodes && hasParent && this.data.level > 0) {
            // Obtener el nivel del PADRE, no del nodo actual
            // Si hay parentNode, usar su nivel; si no, calcular como nivel actual - 1
            let parentLevel: number;
            if (this.data.parentNode?.level !== undefined && this.data.parentNode?.level !== null) {
                parentLevel = this.data.parentNode.level;
            } else {
                // Calcular el nivel del padre como nivel actual - 1
            const currentLevel = this.data.level || 1;
                parentLevel = currentLevel - 1;
            }
            
            // Si no hay parentCodePrefix pero hay parentNode, establecerlo
            if (!this.parentCodePrefix && this.data.parentNode?.code) {
                this.parentCodePrefix = this.data.parentNode.code;
            }
            
            // codigoLista es el ÚNICO campo que indica un subcódigo de la tabla de niveles.
            // La glosa (parte editable del código) es completamente independiente.
            const parentCodigoLista = (this.data.parentNode as any)?.codigoLista;
            const parentHasSubcode = parentCodigoLista !== undefined && parentCodigoLista !== null && parentCodigoLista !== '';

            if (parentHasSubcode) {
                // Buscar en TODOS los niveles activos el registro cuyo code === parentCodigoLista
                this.levelsService.list('', 0, 10000, 'level ASC, code ASC').pipe(takeUntil(this.destroying$)).subscribe({
                    next: (resp: any) => {
                        const allLevels: Level[] = resp?.data || resp || [];
                        const codeToFind = String(parentCodigoLista).trim();
                        const foundParentLevel = allLevels.find(c => {
                            const code = String(c.code || (c as any).Code || '').trim();
                            return code === codeToFind;
                        });

                        if (foundParentLevel) {
                            const parentCodeId = foundParentLevel.id || (foundParentLevel as any).ID;
                            // Buscar hijos directos de este nivel en la tabla
                            const children = allLevels.filter(c => {
                                const pid = c.parent_id || (c as any).ParentID;
                                return pid === parentCodeId;
                            });
                            this.availableCodes = children;
                            this.cdr.markForCheck();
                        } else {
                            this.loadCodesByLevel(1);
                        }
                    },
                    error: () => {
                        this.loadCodesByLevel(1);
                    }
                });
            } else {
                this.loadCodesByLevel(1);
            }
        } else if (isAdministrative && this.hasInheritedCode) {
            // Si es administrativo, no necesitamos cargar códigos
            this.availableCodes = [];
            this.cdr.markForCheck();
        } else {
            // Para otros tipos o si no tiene código heredado, limpiar los códigos disponibles
            this.availableCodes = [];
            this.cdr.markForCheck();
        }
    }

    /**
     * Extrae el código para tipo proyecto usando los códigos disponibles
     * Esta función identifica correctamente el código de la lista desde el final del código
     * Funciona para todos los niveles (1, 2, 3, etc.)
     */
    private extractCodeForProjectTypeWithCodes(): void {
        if (!this.isUpdateMode || !this.data.node?.code || !this.data.parentNode?.code) {
            return;
        }

        const parentCode = this.data.parentNode.code;
        const fullCode = this.data.node.code;

        if (fullCode.startsWith(parentCode)) {
            const remaining = fullCode.substring(parentCode.length);

            // Si el código tiene formato con guiones (padre-glosa-codigoLista)
            if (remaining.startsWith('-')) {
                const codigoRestante = remaining.substring(1);
                
                // Si hay códigos disponibles, intentar identificar el código de la lista desde el final
                if (this.availableCodes.length > 0) {
                    // Buscar el código de la lista desde el final (buscar el más largo primero)
                    const sortedCodes = [...this.availableCodes].sort((a, b) => {
                        const aLen = (a.code || '').length;
                        const bLen = (b.code || '').length;
                        return bLen - aLen;
                    });

                    let codigoLista = '';
                    let glosa = '';

                    // Buscar si el código restante termina con algún código de la lista
                    for (const codeOption of sortedCodes) {
                        const codeValue = codeOption.code || '';
                        if (codeValue && codigoRestante.endsWith(codeValue)) {
                            codigoLista = codeValue;
                            // El resto es la glosa (puede tener guiones)
                            glosa = codigoRestante.substring(0, codigoRestante.length - codeValue.length);
                            // Remover el guion final de la glosa si existe
                            if (glosa.endsWith('-')) {
                                glosa = glosa.substring(0, glosa.length - 1);
                            }
                            break;
                        }
                    }

                    // Si no se encontró código de lista, todo el código restante es la glosa
                    if (!codigoLista) {
                        glosa = codigoRestante;
                    }

                    this.editableCodeSuffix = glosa;
                    this.glosaCode = codigoLista;

                    // Actualizar el formulario
                    this.form.patchValue({
                        codigo: glosa,
                        codigoLista: codigoLista
                    }, { emitEvent: false });
                    
                    // Forzar detección de cambios
                    this.cdr.markForCheck();
                } else {
                    // Si no hay códigos disponibles, asumir que todo es glosa
                    const glosa = codigoRestante;
                    this.editableCodeSuffix = glosa;
                    this.glosaCode = '';

                    // Actualizar el formulario
                    this.form.patchValue({
                        codigo: glosa,
                        codigoLista: ''
                    }, { emitEvent: false });
                    
                    // Forzar detección de cambios
                    this.cdr.markForCheck();
                }
            } else {
                // Formato antiguo sin guiones - usar extractCodeForProjectType
                this.extractCodeForProjectType();
            }
        }
    }

    /**
     * Extrae el código para tipo proyecto después de que los códigos estén cargados
     * Formato: padre-glosa-codigoLista
     * Versión que intenta extraer sin códigos disponibles (formato antiguo)
     */
    private extractCodeForProjectType(): void {
        if (!this.isUpdateMode || !this.data.node?.code || !this.data.parentNode?.code) {
            return;
        }

        const parentCode = this.data.parentNode.code;
        const fullCode = this.data.node.code;

        if (fullCode.startsWith(parentCode)) {
            const remaining = fullCode.substring(parentCode.length);

            // Si el código tiene formato con guiones (padre-glosa-codigoLista)
            if (remaining.startsWith('-')) {
                const parts = remaining.substring(1).split('-');
                if (parts.length >= 2) {
                    // Hay glosa y código de la lista: padre-glosa-codigoLista
                    const glosa = parts[0] || '';
                    const codigoLista = parts.slice(1).join('-');
                    
                    this.editableCodeSuffix = glosa;
                    this.glosaCode = codigoLista;

                    // Actualizar el formulario
                    this.form.patchValue({
                        codigo: glosa,
                        codigoLista: codigoLista
                    }, { emitEvent: false });
                } else if (parts.length === 1) {
                    // Solo hay glosa (sin código de lista): padre-glosa
                    const glosa = parts[0] || '';
                    this.editableCodeSuffix = glosa;
                    this.glosaCode = '';

                    // Actualizar el formulario
                    this.form.patchValue({
                        codigo: glosa,
                        codigoLista: ''
                    }, { emitEvent: false });
                }
                
                // Forzar detección de cambios
                this.cdr.markForCheck();
            } else {
                // Formato antiguo sin guiones - intentar extraer usando los códigos disponibles
                // Solo si los códigos ya están cargados
                if (this.availableCodes.length > 0) {
                    let codigoLista = '';

                    // Buscar el código de la lista desde el final (buscar el más largo primero)
                    const sortedCodes = [...this.availableCodes].sort((a, b) => {
                        const aLen = (a.code || '').length;
                        const bLen = (b.code || '').length;
                        return bLen - aLen;
                    });

                    for (const codeOption of sortedCodes) {
                        const codeValue = codeOption.code || '';
                        if (codeValue && remaining.endsWith(codeValue)) {
                            codigoLista = codeValue;
                            break;
                        }
                    }

                    if (codigoLista) {
                        this.editableCodeSuffix = '';
                        this.glosaCode = codigoLista;

                        // Actualizar el formulario
                        this.form.patchValue({
                            codigo: '',
                            codigoLista: codigoLista
                        }, { emitEvent: false });
                        
                        // Forzar detección de cambios
                        this.cdr.markForCheck();
                    }
                }
            }

            // Forzar detección de cambios
            this.cdr.markForCheck();
        }
    }

    /**
     * Obtiene el código completo para tipo proyecto
     * Formato: Codigo Padre + "-" + Glosa + "-" + Codigo de la lista seleccionable
     * Este es el formato que se guarda en el campo código
     */
    get fullProjectCode(): string {
        if (!this.showLevelsDropdown || !this.hasInheritedCode) {
            return '';
        }

        const parentCode = this.data.parentNode?.code || '';
        const glosa = this.form?.get('codigo')?.value || '';
        const codigoLista = this.form?.get('codigoLista')?.value || '';

        if (!parentCode) {
            return '';
        }

        // Construir el código completo: padre-glosa-codigoLista
        // Si la glosa está vacía, el formato es: padre-codigoLista (sin guion extra)
        const parts: string[] = [parentCode];
        if (glosa && glosa.trim() !== '') {
            parts.push(glosa);
        }
        if (codigoLista && codigoLista.trim() !== '') {
            parts.push(codigoLista);
        }
        return parts.join('-');
    }

    /**
     * Inicializa los observables filtrados para los autocompletes de usuarios
     */
    private initializeUserAutocompletes(): void {
        // Autocomplete para Jefe de Proyecto
        this.filteredJefeProyectoList = this.form.get('id_jefeProyecto')!.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.name;
                return name ? this._filterUser(name as string) : this.userListAuto.slice();
            }),
        );

        // Autocomplete para Control de Proyecto
        this.filteredControlProyectoList = this.form.get('id_controlProyecto')!.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.name;
                return name ? this._filterUser(name as string) : this.userListAuto.slice();
            }),
        );

        // Autocomplete para Control Documental
        this.filteredControlDocumentalList = this.form.get('id_controlDocumental')!.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.name;
                return name ? this._filterUser(name as string) : this.userListAuto.slice();
            }),
        );
    }

    /**
     * Establece los valores iniciales de los usuarios en modo edición
     */
    private setInitialUserValues(): void {
        if (!this.data.node || this.userListAuto.length === 0) {
            return;
        }

        // Buscar y establecer Jefe de Proyecto
        if (this.data.node.id_jefeProyecto) {
            const jefeProyecto = this.userListAuto.find(user => 
                user.ID?.toString() === this.data.node!.id_jefeProyecto
            );
            if (jefeProyecto) {
                this.form.get('id_jefeProyecto')?.setValue(jefeProyecto, { emitEvent: false });
            }
        }

        // Buscar y establecer Control de Proyecto
        if (this.data.node.id_controlProyecto) {
            const controlProyecto = this.userListAuto.find(user => 
                user.ID?.toString() === this.data.node!.id_controlProyecto
            );
            if (controlProyecto) {
                this.form.get('id_controlProyecto')?.setValue(controlProyecto, { emitEvent: false });
            }
        }

        // Buscar y establecer Control Documental
        if (this.data.node.id_controlDocumental) {
            const controlDocumental = this.userListAuto.find(user => 
                user.ID?.toString() === this.data.node!.id_controlDocumental
            );
            if (controlDocumental) {
                this.form.get('id_controlDocumental')?.setValue(controlDocumental, { emitEvent: false });
            }
        }
    }

    /**
     * Filtra la lista de usuarios según el texto ingresado
     */
    private _filterUser(name: string): User[] {
        const filterValue = name.toLowerCase();
        return this.userListAuto.filter(option => 
            option.name?.toLowerCase().indexOf(filterValue) !== -1 ||
            option.firstName?.toLowerCase().indexOf(filterValue) !== -1 ||
            option.lastName?.toLowerCase().indexOf(filterValue) !== -1
        );
    }

    /**
     * Función para mostrar el nombre del usuario en el autocomplete
     */
    displayUserFn(user: User): string {
        if (user) {
            return user.name || (user.lastName + ' ' + user.firstName) || '';
        }
        return '';
    }

    /**
     * Limpia el campo de usuario especificado
     */
    resetUser(fieldName: 'id_jefeProyecto' | 'id_controlProyecto' | 'id_controlDocumental'): void {
        this.form.get(fieldName)?.setValue(null);
    }

    /**
     * Obtiene el objeto User por su ID
     */
    private getUserObjectById(userId: string | undefined): User | null {
        if (!userId || this.userListAuto.length === 0) {
            return null;
        }
        return this.userListAuto.find(user => 
            user.ID?.toString() === userId
        ) || null;
    }

    /**
     * Carga proyectos sin padre (parent_id es null o 0) para seleccionar al crear un hijo
     */
    private loadProjectsWithoutParent(): void {
        // Buscar proyectos cuyo parent_id sea null o 0
        const searchObj = { 
            parentID: null,
            parentId: null,
            parent_id: null
        };
        const searchString = JSON.stringify(searchObj);

        this.projectsService.list(searchString, 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }

                // Filtrar solo proyectos sin padre (parent_id es null, 0, o undefined)
                const projectsWithoutParent = projects.filter((project: any) => {
                    const parentId = project.parent_id || project.ParentID || project.parentId || project.ParentId;
                    return !parentId || parentId === 0 || parentId === '0' || parentId === null;
                });

                // Mapear a ProjectTreeNode
                this.availableProjectsWithoutParent = projectsWithoutParent.map((project: any) => {
                    const frontendData = mapBackendToFrontend(project);
                    return {
                        id: frontendData.id || project.id?.toString() || '',
                        name: frontendData.proyecto || project.name || '',
                        code: frontendData.codigo || project.code || '',
                        description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                        level: frontendData.level || project.level || 0,
                        parentId: undefined, // Sin padre
                        expanded: false,
                        hasChildren: false,
                        children: [],
                        active: frontendData.activo !== false,
                        aprobado: frontendData.aprobado || false,
                        tipo: frontendData.tipo || project.tipo || project.Tipo || '',
                        id_cliente: frontendData.id_cliente || project.id_cliente || project.IdCliente || '',
                        origen: frontendData.origen || project.origen || project.Origen || '',
                        moneda: frontendData.moneda,
                        pep: frontendData.pep,
                        fechaInicio: frontendData.fechaInicio,
                        fechaTermino: frontendData.fechaTermino,
                        fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                        id_jefeProyecto: frontendData.id_jefeProyecto,
                        id_controlProyecto: frontendData.id_controlProyecto,
                        id_controlDocumental: frontendData.id_controlDocumental,
                        HHEstimadas: frontendData.HHEstimadas,
                        requiereObservacion: frontendData.requiereObservacion || false,
                        horasExtras: frontendData.horasExtras !== false,
                        horasNormales: frontendData.horasNormales !== false
                    } as ProjectTreeNode;
                });

                // Inicializar el autocomplete después de cargar
                this.existingProjectInputControl.setValue('', { emitEvent: false });
            },
            error: (err) => {
                console.error('Error al cargar proyectos sin padre:', err);
                this.availableProjectsWithoutParent = [];
            }
        });
    }

    /**
     * Filtra proyectos sin padre según el texto de búsqueda
     */
    private _filterProjectsWithoutParent(searchTerm: string): ProjectTreeNode[] {
        if (!searchTerm) {
            return this.availableProjectsWithoutParent.slice();
        }
        const filterValue = searchTerm.toLowerCase();
        return this.availableProjectsWithoutParent.filter(project =>
            project.name?.toLowerCase().includes(filterValue) ||
            project.code?.toLowerCase().includes(filterValue) ||
            (project.code + ' - ' + project.name).toLowerCase().includes(filterValue)
        );
    }

    /**
     * Función para mostrar el proyecto en el autocomplete
     */
    displayProjectFn(project: ProjectTreeNode | string): string {
        if (typeof project === 'string') {
            return project;
        }
        if (project) {
            return `${project.code} - ${project.name}`;
        }
        return '';
    }

    /**
     * Muestra el proyecto padre en el autocomplete del tab Padre/Hijos
     */
    displayParentFn(project: ProjectTreeNode | string | null): string {
        if (project == null) {
            return '';
        }
        if (typeof project === 'string') {
            return project;
        }
        return `${project.code} - ${project.name}`;
    }

    /**
     * Carga todos los proyectos que pueden ser padre (para el autocomplete del tab Padre/Hijos).
     * Excluye el nodo actual para evitar ciclos.
     */
    private loadAvailableParents(): void {
        this.projectsService.list('', 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }
                const currentId = this.data.node?.id?.toString();
                const mapped = projects
                    .filter((p: any) => {
                        const id = (p.id ?? p.ID)?.toString();
                        return id && id !== currentId;
                    })
                    .map((project: any) => {
                        const frontendData = mapBackendToFrontend(project);
                        return {
                            id: frontendData.id || project.id?.toString() || '',
                            name: frontendData.proyecto || project.name || '',
                            code: frontendData.codigo || project.code || '',
                            level: frontendData.level ?? project.level ?? 0,
                            parentId: frontendData.parentId || (project.parent_id ? project.parent_id.toString() : undefined),
                            expanded: false,
                            hasChildren: false,
                            children: [],
                            active: frontendData.activo !== false
                        } as ProjectTreeNode;
                    });
                this.availableParentList = mapped;
                if (this.data.parentNode) {
                    const currentParent = this.availableParentList.find(p => p.id === this.data.parentNode?.id);
                    this.parentAutocompleteControl.setValue(currentParent ?? this.data.parentNode, { emitEvent: false });
                } else {
                    this.parentAutocompleteControl.setValue(null, { emitEvent: false });
                }
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Error al cargar proyectos para selector de padre:', err);
                this.availableParentList = [];
            }
        });
    }

    /**
     * Filtra la lista de padres según el texto del autocomplete
     */
    private _filterParentList(searchTerm: string): ProjectTreeNode[] {
        if (!searchTerm) {
            return this.availableParentList.slice();
        }
        const filterValue = searchTerm.toLowerCase();
        return this.availableParentList.filter(project =>
            project.name?.toLowerCase().includes(filterValue) ||
            project.code?.toLowerCase().includes(filterValue) ||
            `${project.code || ''} - ${project.name || ''}`.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Al seleccionar un proyecto como padre en el tab Padre/Hijos
     */
    onParentSelected(event: MatAutocompleteSelectedEvent): void {
        const selected = event.option.value as ProjectTreeNode;
        (this.data as any).parentExplicitlyCleared = false;
        this.data.parentNode = selected;
        this.data.parentId = selected.id;
        this.parentCodePrefix = selected.code || '';
        // Recargar códigos disponibles (terreno, viaje, oficina) para el hijo según el nuevo padre
        this.updateCodeFieldBasedOnType(this.form?.get('tipo')?.value);
        this.cdr.markForCheck();
    }

    /**
     * Quita la selección de padre (el proyecto pasará a ser raíz al guardar)
     */
    clearParentSelection(): void {
        this.data.parentNode = undefined;
        this.data.parentId = undefined;
        (this.data as any).parentExplicitlyCleared = true;
        this.parentCodePrefix = '';
        this.parentAutocompleteControl.setValue(null, { emitEvent: false });
        this.updateCodeFieldBasedOnType(this.form?.get('tipo')?.value);
        this.cdr.markForCheck();
    }

    /**
     * Maneja la selección de un proyecto desde el autocomplete
     */
    onProjectAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
        const selectedProject = event.option.value as ProjectTreeNode;
        this.selectedExistingProject = selectedProject;
        this.form.get('existingProjectId')?.setValue(selectedProject.id);
        this.existingProjectInputControl.setValue(this.displayProjectFn(selectedProject), { emitEvent: false });
        
        // Pre-llenar el formulario inmediatamente con los datos del proyecto seleccionado
        this.fillFormWithProjectData(selectedProject);
    }

    /**
     * Maneja la selección de un proyecto existente
     */
    private onExistingProjectSelected(projectId: string): void {
        const selectedProject = this.availableProjectsWithoutParent.find(p => p.id === projectId);
        if (!selectedProject) {
            return;
        }

        this.selectedExistingProject = selectedProject;
        this.fillFormWithProjectData(selectedProject);
    }

    /**
     * Pre-llena el formulario con los datos del proyecto seleccionado
     */
    private fillFormWithProjectData(selectedProject: ProjectTreeNode): void {
        // Esperar a que los usuarios estén cargados antes de asignar los campos de usuario
        if (this.userListAuto.length === 0) {
            // Si los usuarios no están cargados, esperar un momento y reintentar
            setTimeout(() => {
                this.fillFormWithProjectData(selectedProject);
            }, 100);
            return;
        }

        // Pre-llenar el formulario con los datos del proyecto seleccionado
        // Pero mantener el parentId como el padre actual
        this.form.patchValue({
            proyecto: selectedProject.name,
            tipo: selectedProject.tipo || '',
            id_cliente: selectedProject.id_cliente || '',
            origen: selectedProject.origen || '',
            moneda: selectedProject.moneda || '',
            pep: selectedProject.pep || '',
            aprobado: selectedProject.aprobado || false,
            activo: selectedProject.active !== false,
            requiereObservacion: selectedProject.requiereObservacion || false,
            horasExtras: selectedProject.horasExtras || false,
            horasNormales: selectedProject.horasNormales !== false,
            fechaInicio: selectedProject.fechaInicio || '',
            fechaTermino: selectedProject.fechaTermino || '',
            fechaEstimadaTermino: selectedProject.fechaEstimadaTermino || '',
            HHEstimadas: selectedProject.HHEstimadas || null,
            id_jefeProyecto: this.getUserObjectById(selectedProject.id_jefeProyecto),
            id_controlProyecto: this.getUserObjectById(selectedProject.id_controlProyecto),
            id_controlDocumental: this.getUserObjectById(selectedProject.id_controlDocumental)
        }, { emitEvent: false });

        // Manejar el código del proyecto seleccionado
        // Si el proyecto tiene un código, necesitamos extraer la parte editable
        if (selectedProject.code && this.data.parentNode) {
            const parentCode = this.data.parentNode.code || '';
            if (parentCode && selectedProject.code.startsWith(parentCode)) {
                // El código del proyecto seleccionado ya tiene el prefijo del padre
                // Extraer solo la parte editable
                const remaining = selectedProject.code.substring(parentCode.length);
                if (remaining.startsWith('-')) {
                    const parts = remaining.substring(1).split('-');
                    if (parts.length >= 2) {
                        // Tiene glosa y código de lista
                        this.editableCodeSuffix = parts[0] || '';
                        this.glosaCode = parts.slice(1).join('-');
                    } else if (parts.length === 1) {
                        // Solo tiene glosa o código de lista
                        this.editableCodeSuffix = parts[0] || '';
                        this.glosaCode = '';
                    }
                } else {
                    // No tiene guion, usar todo como editable
                    this.editableCodeSuffix = remaining;
                    this.glosaCode = '';
                }
            } else {
                // El código no tiene el prefijo del padre, usar todo como editable
                this.editableCodeSuffix = selectedProject.code;
                this.glosaCode = '';
            }
        } else {
            // No hay código o no hay padre, usar valores por defecto
            this.editableCodeSuffix = '';
            this.glosaCode = '';
        }

        // Actualizar el código según el tipo y el padre
        if (selectedProject.tipo) {
            this.updateCodeFieldBasedOnType(selectedProject.tipo);
        }

        // Actualizar los campos del formulario con los valores del código
        this.form.patchValue({
            codigo: this.editableCodeSuffix,
            codigoLista: this.glosaCode
        }, { emitEvent: false });

        // Forzar actualización de la vista
        this.cdr.markForCheck();
        this.cdr.detectChanges();
    }

    /**
     * Limpia la selección de proyecto existente
     */
    clearExistingProjectSelection(): void {
        this.form.get('existingProjectId')?.setValue(null);
        this.selectedExistingProject = null;
        this.existingProjectInputControl.setValue('', { emitEvent: false });
        // Limpiar el formulario excepto los campos que ya estaban
        this.form.patchValue({
            proyecto: '',
            tipo: '',
            id_cliente: '',
            origen: '',
            moneda: '',
            pep: '',
            aprobado: false,
            activo: true,
            requiereObservacion: false,
            horasExtras: false,
            horasNormales: true,
            fechaInicio: '',
            fechaTermino: '',
            fechaEstimadaTermino: '',
            HHEstimadas: null,
            id_jefeProyecto: null,
            id_controlProyecto: null,
            id_controlDocumental: null
        });
    }

    /**
     * Elimina el padre del proyecto actual (solo en modo edición)
     */
    removeParent(): void {
        if (!this.isUpdateMode || !this.data.node) {
            return;
        }

        const confirmMessage = this.translateService.instant('MESSAGES.CONFIRM_REMOVE_PARENT') || 
                              '¿Está seguro de que desea eliminar el padre de este proyecto?';
        
        const dialogRef = this.dialog.open(DialogComponent, {
            width: '400px',
            data: {
                title: this.translateService.instant('ACTIONS.ELIMINAR_PADRE') || 'Eliminar Padre',
                message: confirmMessage,
                confirmText: this.translateService.instant('ACTIONS.CONFIRMAR') || 'Confirmar',
                cancelText: this.translateService.instant('ACTIONS.CANCELAR') || 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.performRemoveParent();
            }
        });
    }

    /**
     * Realiza la eliminación del padre
     */
    private performRemoveParent(): void {
        if (!this.data.node) {
            return;
        }

        this.isLoading = true;

        // Obtener los datos actuales del formulario (getRawValue incluye campos disabled como tipo)
        const formValue = this.form.getRawValue();
        
        // Construir los datos para actualizar sin parent_id
        const formData: ProjectFormData = {
            id: this.data.node.id,
            proyecto: formValue.proyecto,
            codigo: this.data.node.code || formValue.codigo, // Mantener el código actual
            codigoPadre: '', // Sin padre
            codigoGlosa: (this.data.node as any).codigoGlosa || '',
            codigoLista: (this.data.node as any).codigoLista || '',
            pep: formValue.pep || '',
            aprobado: formValue.aprobado || false,
            activo: formValue.activo !== false,
            visible: formValue.visible !== false,
            requiereObservacion: formValue.requiereObservacion || false,
            horasExtras: formValue.horasExtras || false,
            horasNormales: formValue.horasNormales !== false,
            tipo: formValue.tipo,
            id_cliente: formValue.id_cliente,
            origen: formValue.origen,
            moneda: formValue.moneda,
            fechaInicio: formValue.fechaInicio || '',
            fechaTermino: formValue.fechaTermino || '',
            fechaEstimadaTermino: formValue.fechaEstimadaTermino || '',
            id_jefeProyecto: (formValue.id_jefeProyecto?.ID?.toString() || formValue.id_jefeProyecto || ''),
            id_controlProyecto: (formValue.id_controlProyecto?.ID?.toString() || formValue.id_controlProyecto || ''),
            id_controlDocumental: (formValue.id_controlDocumental?.ID?.toString() || formValue.id_controlDocumental || ''),
            HHEstimadas: formValue.HHEstimadas || null,
            parentId: undefined, // Sin padre
            level: 0 // Nivel raíz
        };

        // Mapear a formato del backend
        const backendData = mapFrontendToBackend(formData);
        
        // Asegurar que parent_id sea null
        backendData.parent_id = null;

        const updatePayload = [backendData];

        this.projectsService.update(updatePayload).subscribe({
            next: (response) => {
                this.isLoading = false;
                const backendResponse = Array.isArray(response) ? response[0] : response;
                const frontendData = mapBackendToFrontend(backendResponse);
                
                this.handleSuccessResponse('Padre eliminado correctamente');
                
                // Cerrar el diálogo y retornar los datos actualizados
                this.dialogRef.close(frontendData);
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Error al eliminar el padre:', error);
                this.handleErrorResponse(error);
            }
        });
    }
}
